/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import { __decorate, __metadata } from 'tslib';
import { Input, HostBinding, Directive, ElementRef, Renderer2, NgZone, forwardRef, Component, NgModule, EventEmitter, isDevMode, Output, ContentChild, ChangeDetectorRef, ViewChild } from '@angular/core';
import { isDocumentAvailable, guid, hasObservers, KendoInput } from '@progress/kendo-angular-common';
import { ComponentMessages, LocalizationService, L10N_PREFIX } from '@progress/kendo-angular-l10n';
import { CommonModule } from '@angular/common';
import { NgControl } from '@angular/forms';
import { validatePackage } from '@progress/kendo-licensing';
import { Observable, Subscription } from 'rxjs';

/**
 * @hidden
 */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@progress/kendo-angular-l10n';
import * as ɵngcc2 from '@angular/common';

var I18N_0;
if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
    /**
     * @desc The text for the optional segment of a FloatingLabel component
     * @meaning kendo.floatinglabel.optional
     */ 
    const MSG_EXTERNAL_356911862062553631$$DIST_FESM2015_INDEX_JS_1 = goog.getMsg("Optional");
    I18N_0 = MSG_EXTERNAL_356911862062553631$$DIST_FESM2015_INDEX_JS_1;
}
else {
    I18N_0 = $localize `:kendo.floatinglabel.optional|The text for the optional segment of a FloatingLabel component␟d6b1dd6fb7530bd600b430042425d10b22163d1f␟356911862062553631:Optional`;
}
const _c2 = ["optional", I18N_0];
function FloatingLabelComponent_label_3_span_2_Template(rf, ctx) { if (rf & 1) {
    ɵngcc0.ɵɵelementStart(0, "span", 4);
    ɵngcc0.ɵɵtext(1);
    ɵngcc0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = ɵngcc0.ɵɵnextContext(2);
    ɵngcc0.ɵɵadvance(1);
    ɵngcc0.ɵɵtextInterpolate1("(", ctx_r1.textFor("optional"), ")");
} }
function FloatingLabelComponent_label_3_Template(rf, ctx) { if (rf & 1) {
    ɵngcc0.ɵɵelementStart(0, "label", 2);
    ɵngcc0.ɵɵtext(1);
    ɵngcc0.ɵɵtemplate(2, FloatingLabelComponent_label_3_span_2_Template, 2, 1, "span", 3);
    ɵngcc0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = ɵngcc0.ɵɵnextContext();
    ɵngcc0.ɵɵproperty("ngClass", ctx_r0.labelCssClass)("ngStyle", ctx_r0.labelCssStyle)("for", ctx_r0.id);
    ɵngcc0.ɵɵattribute("id", ctx_r0.labelId);
    ɵngcc0.ɵɵadvance(1);
    ɵngcc0.ɵɵtextInterpolate1(" ", ctx_r0.text, "");
    ɵngcc0.ɵɵadvance(1);
    ɵngcc0.ɵɵproperty("ngIf", ctx_r0.optional);
} }
const _c3 = ["*"];
var I18N_4;
if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
    /**
     * @desc The text for the optional segment of a Label component
     * @meaning kendo.label.optional
     */ 
    const MSG_EXTERNAL_3476218486854814180$$DIST_FESM2015_INDEX_JS_5 = goog.getMsg("Optional");
    I18N_4 = MSG_EXTERNAL_3476218486854814180$$DIST_FESM2015_INDEX_JS_5;
}
else {
    I18N_4 = $localize `:kendo.label.optional|The text for the optional segment of a Label component␟a1cd4a8f359a22151d7e73f2887fbb2967cd389a␟3476218486854814180:Optional`;
}
const _c6 = ["optional", I18N_4];
function LabelComponent_span_4_Template(rf, ctx) { if (rf & 1) {
    ɵngcc0.ɵɵelementStart(0, "span", 3);
    ɵngcc0.ɵɵtext(1);
    ɵngcc0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = ɵngcc0.ɵɵnextContext();
    ɵngcc0.ɵɵadvance(1);
    ɵngcc0.ɵɵtextInterpolate1("(", ctx_r0.textFor("optional"), ")");
} }
const isUploadComponent = (component) => component.wrapper && (component.wrapper.tagName === 'KENDO-UPLOAD' || component.wrapper.tagName === 'KENDO-FILESELECT');
/**
 * @hidden
 */
const isInputElement = (component) => component instanceof HTMLElement;
/**
 * @hidden
 */
const inputElementHasAttr = (element, attribute) => element.hasAttribute(attribute);
/**
 * @hidden
 */
const getWrappedNativeInput = (element) => element.querySelector('kendo-label > input, kendo-label > textarea, kendo-label > select');
/**
 * @hidden
 */
const getRootElement = (element) => {
    if (!element) {
        return null;
    }
    let rootElement = element;
    while (rootElement.parentElement) {
        rootElement = rootElement.parentElement;
    }
    return rootElement;
};

/**
 * Represents the [Kendo UI Label directive for Angular]({% slug overview_label %}).
 * The Label associates a focusable Angular component or an HTML element
 * with a `label` tag by using the `[for]` property binding.
 *
 * To associate a component by using the `label` element, either:
 * * Set the `[for]` property binding to a
 * [template reference variable]({{ site.data.urls.angular['templatesyntax'] }}#template-reference-variables--var-), or
 * * Set the `[for]` property binding to an `id` HTML string value.
 *
 * @example
 * ```ts
 * _@Component({
 * selector: 'my-app',
 * template: `
 *  <div class="row example-wrapper" style="min-height: 450px;">
 *    <div class="col-xs-12 col-md-6 example-col">
 *      <label [for]="datepicker">DatePicker: </label>
 *      <kendo-datepicker #datepicker></kendo-datepicker>
 *    </div>
 *
 *    <div class="col-xs-12 col-md-6 example-col">
 *      <label for="input">Input: </label>
 *      <input id="input" />
 *    </div>
 *  </div>
 * `
 * })
 * class AppComponent { }
 * ```
 */
let LabelDirective = class LabelDirective {
    constructor(label, renderer, zone) {
        this.label = label;
        this.renderer = renderer;
        this.zone = zone;
        this.labelClass = true;
        this.handleClick = () => {
            const component = this.getFocusableComponent();
            if (!component) {
                return;
            }
            if (isUploadComponent(component)) {
                component.fileSelect.nativeElement.click();
            }
            if (component.focus) {
                component.focus();
            }
        };
    }
    get labelFor() {
        if (typeof this.for === 'string') {
            return this.for;
        }
        if (!isDocumentAvailable()) {
            return null;
        }
        const component = this.getFocusableComponent() || {};
        if (isInputElement(component) && !inputElementHasAttr(component, 'id')) {
            this.renderer.setAttribute(component, 'id', `k-${guid()}`);
        }
        return component.focusableId || component.id || null;
    }
    /**
     * @hidden
     */
    ngAfterViewInit() {
        this.setAriaLabelledby();
        this.zone.runOutsideAngular(() => this.clickListener = this.renderer.listen(this.label.nativeElement, 'click', this.handleClick));
    }
    /**
     * @hidden
     */
    ngOnDestroy() {
        if (this.clickListener) {
            this.clickListener();
        }
    }
    /**
     * @hidden
     */
    setAriaLabelledby() {
        if (!isDocumentAvailable()) {
            return;
        }
        const component = this.getFocusableComponent();
        if (component && component.focusableId) {
            const rootElement = getRootElement(this.label.nativeElement);
            const labelTarget = rootElement.querySelector(`#${component.focusableId}`);
            if (!labelTarget) {
                return;
            }
            const labelElement = this.label.nativeElement;
            const id = labelElement.id || `k-${guid()}`;
            if (!labelElement.getAttribute('id')) {
                this.renderer.setAttribute(labelElement, 'id', id);
            }
            this.renderer.setAttribute(labelTarget, 'aria-labelledby', id);
        }
    }
    getFocusableComponent() {
        const target = this.for;
        return target && target.focus !== undefined ? target : null;
    }
};
LabelDirective.ɵfac = function LabelDirective_Factory(t) { return new (t || LabelDirective)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.Renderer2), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.NgZone)); };
LabelDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: LabelDirective, selectors: [["label", "for", ""]], hostVars: 3, hostBindings: function LabelDirective_HostBindings(rf, ctx) { if (rf & 2) {
        ɵngcc0.ɵɵattribute("for", ctx.labelFor);
        ɵngcc0.ɵɵclassProp("k-label", ctx.labelClass);
    } }, inputs: { for: "for" } });
__decorate([
    Input(),
    __metadata("design:type", Object)
], LabelDirective.prototype, "for", void 0);
__decorate([
    HostBinding('attr.for'),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], LabelDirective.prototype, "labelFor", null);
__decorate([
    HostBinding('class.k-label'),
    __metadata("design:type", Boolean)
], LabelDirective.prototype, "labelClass", void 0);
LabelDirective = __decorate([ __metadata("design:paramtypes", [ElementRef, Renderer2, NgZone])
], LabelDirective);

/**
 * @hidden
 */
class Messages extends ComponentMessages {
}
Messages.ɵfac = function Messages_Factory(t) { return ɵMessages_BaseFactory(t || Messages); };
Messages.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: Messages, inputs: { optional: "optional" }, features: [ɵngcc0.ɵɵInheritDefinitionFeature] });
__decorate([
    Input(),
    __metadata("design:type", String)
], Messages.prototype, "optional", void 0);

var LocalizedMessagesDirective_1;
/**
 * @hidden
 */
let LocalizedMessagesDirective = LocalizedMessagesDirective_1 = class LocalizedMessagesDirective extends Messages {
    constructor(service) {
        super();
        this.service = service;
    }
};
LocalizedMessagesDirective.ɵfac = function LocalizedMessagesDirective_Factory(t) { return new (t || LocalizedMessagesDirective)(ɵngcc0.ɵɵdirectiveInject(ɵngcc1.LocalizationService)); };
LocalizedMessagesDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: LocalizedMessagesDirective, selectors: [["", "kendoLabelLocalizedMessages", ""], ["", "kendoFloatingLabelLocalizedMessages", ""]], features: [ɵngcc0.ɵɵProvidersFeature([
            {
                provide: Messages,
                useExisting: forwardRef(() => LocalizedMessagesDirective_1)
            }
        ]), ɵngcc0.ɵɵInheritDefinitionFeature] });
LocalizedMessagesDirective = LocalizedMessagesDirective_1 = __decorate([ __metadata("design:paramtypes", [LocalizationService])
], LocalizedMessagesDirective);

var CustomMessagesComponent_1;
/**
 * Custom component messages override default component messages
 * ([see example]({% slug label_globalization %}#toc-localization)).
 */
let CustomMessagesComponent = CustomMessagesComponent_1 = class CustomMessagesComponent extends Messages {
    constructor(service) {
        super();
        this.service = service;
    }
    get override() {
        return true;
    }
};
CustomMessagesComponent.ɵfac = function CustomMessagesComponent_Factory(t) { return new (t || CustomMessagesComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc1.LocalizationService)); };
CustomMessagesComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: CustomMessagesComponent, selectors: [["kendo-label-messages"], ["kendo-floatinglabel-messages"]], features: [ɵngcc0.ɵɵProvidersFeature([
            {
                provide: Messages,
                useExisting: forwardRef(() => CustomMessagesComponent_1)
            }
        ]), ɵngcc0.ɵɵInheritDefinitionFeature], decls: 0, vars: 0, template: function CustomMessagesComponent_Template(rf, ctx) { }, encapsulation: 2 });
CustomMessagesComponent = CustomMessagesComponent_1 = __decorate([ __metadata("design:paramtypes", [LocalizationService])
], CustomMessagesComponent);

const SHARED_DIRECTIVES = [
    LocalizedMessagesDirective,
    CustomMessagesComponent
];
/**
 * @hidden
 */
let SharedDirectivesModule = class SharedDirectivesModule {
};
SharedDirectivesModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: SharedDirectivesModule });
SharedDirectivesModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function SharedDirectivesModule_Factory(t) { return new (t || SharedDirectivesModule)(); } });

/**
 * @hidden
 */
const packageMetadata = {
    name: '@progress/kendo-angular-label',
    productName: 'Kendo UI for Angular',
    productCodes: ['KENDOUIANGULAR', 'KENDOUICOMPLETE'],
    publishDate: 1646219058,
    version: '',
    licensingDocsUrl: 'https://www.telerik.com/kendo-angular-ui/my-license/?utm_medium=product&utm_source=kendoangular&utm_campaign=kendo-ui-angular-purchase-license-keys-warning'
};

/**
 * @hidden
 */
class FloatingLabelInputAdapter {
    constructor(component, formControl) {
        this.component = component;
        const isObservableOrEventEmitter = (event) => event instanceof Observable || event instanceof EventEmitter;
        if (isObservableOrEventEmitter(component.onFocus)) {
            this.onFocus = component.onFocus;
        }
        if (isObservableOrEventEmitter(component.autoFillStart)) {
            this.autoFillStart = component.autoFillStart;
        }
        if (isObservableOrEventEmitter(component.autoFillEnd)) {
            this.autoFillEnd = component.autoFillEnd;
        }
        if (isObservableOrEventEmitter(component.onBlur)) {
            this.onBlur = component.onBlur;
        }
        if (formControl) {
            this.onValueChange = formControl.valueChanges;
        }
        else if (component.onValueChange) {
            this.onValueChange = component.onValueChange;
        }
    }
    get focusableId() {
        const component = this.component;
        if ('focusableId' in component) {
            return component.focusableId;
        }
        else if ('id' in component) {
            return component.id;
        }
        return "";
    }
    set focusableId(value) {
        const component = this.component;
        if ('focusableId' in component) {
            component.focusableId = value;
        }
        else if ('id' in component) {
            component.id = value;
        }
    }
}

const isFunction = (x) => Object.prototype.toString.call(x) === '[object Function]';
/**
 * Represents the [Kendo UI FloatingLabel component for Angular]({% slug overview_floatinglabel %}).
 * Provides floating labels to `input` elements.
 *
 * The FloatingLabel supports both Template and Reactive Forms and
 * [can contain Kendo UI for Angular Input components such as `kendo-combobox` and `kendo-numerictextbox`,
 * or HTML Input elements with the `kendoTextBox` directive applied]({% slug overview_floatinglabel %}#toc-implementing-floating-labels).
 *
 * @example
 * ```ts
 *
 * _@Component({
 *   selector: 'my-app',
 *   template: `
 *     <kendo-floatinglabel text="First name">
 *       <input [(ngModel)]="name" kendoTextBox />
 *     </kendo-floatinglabel>
 *   `
 * })
 * class AppComponent {
 *     public name = 'John';
 * }
 *
 * ```
 */
let FloatingLabelComponent = class FloatingLabelComponent {
    constructor(elementRef, renderer, changeDetectorRef, localization) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.changeDetectorRef = changeDetectorRef;
        this.localization = localization;
        this.hostClasses = true;
        /**
         * Fires after the floating label position is changed.
         */
        this.positionChange = new EventEmitter();
        /**
         * @hidden
         */
        this.focused = false;
        /**
         * @hidden
         */
        this.empty = true;
        /**
         * @hidden
         */
        this.invalid = false;
        /**
         * @hidden
         */
        this.labelId = `k-${guid()}`;
        this.autoFillStarted = false;
        validatePackage(packageMetadata);
        this.direction = localization.rtl ? 'rtl' : 'ltr';
        this.renderer.removeAttribute(this.elementRef.nativeElement, "id");
    }
    /**
     * Represents the current floating label position.
     */
    get labelPosition() {
        if (!this.empty) {
            return 'Out';
        }
        return this.focused ? 'Out' : 'In';
    }
    get focusedClass() {
        return this.focused;
    }
    get invalidClass() {
        return this.invalid;
    }
    /**
     * @hidden
     */
    ngAfterContentInit() {
        this.validateSetup();
        const control = new FloatingLabelInputAdapter(this.kendoInput || this.formControl.valueAccessor, this.formControl);
        this.addHandlers(control);
        this.setLabelFor(control);
    }
    ngAfterViewInit() {
        if (this.kendoInput) {
            this.setAriaLabelledby(this.kendoInput);
        }
    }
    /**
     * @hidden
     */
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    /**
     * @hidden
     */
    textFor(key) {
        return this.localization.get(key);
    }
    subscribe(control, eventName, handler) {
        if (control[eventName] instanceof EventEmitter) {
            const subscription = control[eventName].subscribe(handler);
            if (!this.subscription) {
                this.subscription = subscription;
            }
            else {
                this.subscription.add(subscription);
            }
        }
    }
    updateState() {
        const empty = value => {
            // zero is not an empty value (e.g., NumericTextBox)
            if (value === 0 || value === false) {
                return false;
            }
            // empty arrays are an empty value (e.g., MultiSelect)
            if (Array.isArray(value) && !value.length) {
                return true;
            }
            return !value;
        };
        const formControl = this.formControl;
        if (formControl) {
            const valueAccessor = formControl.valueAccessor;
            if (isFunction(valueAccessor.isEmpty)) {
                this.empty = valueAccessor.isEmpty();
            }
            else {
                this.empty = empty(formControl.value);
            }
            this.invalid = formControl.invalid && (formControl.touched || formControl.dirty);
        }
        else {
            this.empty = isFunction(this.kendoInput.isEmpty) ?
                this.kendoInput.isEmpty() : empty(this.kendoInput.value);
        }
        if (this.empty) {
            this.renderer.addClass(this.elementRef.nativeElement, 'k-state-empty');
        }
        else {
            this.renderer.removeClass(this.elementRef.nativeElement, 'k-state-empty');
        }
        this.changeDetectorRef.markForCheck();
    }
    setAriaLabelledby(component) {
        const componentId = component.focusableId || component.id;
        if (componentId) {
            const focusableElement = this.elementRef.nativeElement.querySelector(`#${componentId}`);
            this.renderer.setAttribute(focusableElement, 'aria-labelledby', this.labelId);
        }
    }
    setLabelFor(control) {
        const controlId = control.focusableId || control.id;
        if (this.id && controlId) {
            // input wins
            this.id = controlId;
        }
        else if (this.id) {
            control.focusableId = this.id;
        }
        else if (controlId) {
            this.id = controlId;
        }
        else {
            const id = `k-${guid()}`;
            control.focusableId = id;
            this.id = id;
        }
    }
    handleAutofill(control) {
        this.subscribe(control, 'autoFillStart', () => {
            this.autoFillStarted = true;
            this.renderer.removeClass(this.elementRef.nativeElement, 'k-state-empty');
        });
        this.subscribe(control, 'autoFillEnd', () => {
            if (this.autoFillStarted) {
                this.autoFillStarted = false;
                if (this.empty) {
                    this.renderer.addClass(this.elementRef.nativeElement, 'k-state-empty');
                }
            }
        });
    }
    addHandlers(control) {
        const setFocus = (isFocused) => () => {
            this.focused = isFocused;
            this.updateState();
            if (!this.empty) {
                return;
            }
            if (hasObservers(this.positionChange)) {
                this.positionChange.emit(isFocused ? 'Out' : 'In');
            }
        };
        this.subscribe(control, 'onFocus', setFocus(true));
        this.subscribe(control, 'onBlur', setFocus(false));
        this.handleAutofill(control);
        const updateState = () => this.updateState();
        updateState();
        this.subscribe(control, 'onValueChange', updateState);
    }
    validateSetup() {
        if (!this.formControl && !this.kendoInput) {
            if (isDevMode()) {
                throw new Error("The FloatingLabelComponent requires a Kendo Input component" +
                    " or a forms-bound component to function properly.");
            }
            return;
        }
    }
};
FloatingLabelComponent.ɵfac = function FloatingLabelComponent_Factory(t) { return new (t || FloatingLabelComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.Renderer2), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ChangeDetectorRef), ɵngcc0.ɵɵdirectiveInject(ɵngcc1.LocalizationService)); };
FloatingLabelComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: FloatingLabelComponent, selectors: [["kendo-floatinglabel"]], contentQueries: function FloatingLabelComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        ɵngcc0.ɵɵcontentQuery(dirIndex, KendoInput, true);
        ɵngcc0.ɵɵcontentQuery(dirIndex, NgControl, true);
    } if (rf & 2) {
        var _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.kendoInput = _t.first);
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.formControl = _t.first);
    } }, hostVars: 7, hostBindings: function FloatingLabelComponent_HostBindings(rf, ctx) { if (rf & 2) {
        ɵngcc0.ɵɵattribute("dir", ctx.direction);
        ɵngcc0.ɵɵclassProp("k-floating-label-container", ctx.hostClasses)("k-state-focused", ctx.focusedClass)("k-state-invalid", ctx.invalidClass);
    } }, inputs: { id: "id", labelCssStyle: "labelCssStyle", labelCssClass: "labelCssClass", text: "text", optional: "optional" }, outputs: { positionChange: "positionChange" }, exportAs: ["kendoFloatingLabel"], features: [ɵngcc0.ɵɵProvidersFeature([
            LocalizationService,
            {
                provide: L10N_PREFIX,
                useValue: 'kendo.floatinglabel'
            }
        ])], ngContentSelectors: _c3, decls: 4, vars: 1, consts: [["kendoFloatingLabelLocalizedMessages", "", 6, "optional"], ["class", "k-label", 3, "ngClass", "ngStyle", "for", 4, "ngIf"], [1, "k-label", 3, "ngClass", "ngStyle", "for"], ["class", "k-label-optional", 4, "ngIf"], [1, "k-label-optional"]], template: function FloatingLabelComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵprojectionDef();
        ɵngcc0.ɵɵelementContainerStart(0, 0);
        ɵngcc0.ɵɵi18nAttributes(1, _c2);
        ɵngcc0.ɵɵelementContainerEnd();
        ɵngcc0.ɵɵprojection(2);
        ɵngcc0.ɵɵtemplate(3, FloatingLabelComponent_label_3_Template, 3, 6, "label", 1);
    } if (rf & 2) {
        ɵngcc0.ɵɵadvance(3);
        ɵngcc0.ɵɵproperty("ngIf", ctx.text);
    } }, directives: [LocalizedMessagesDirective, ɵngcc2.NgIf, ɵngcc2.NgClass, ɵngcc2.NgStyle], encapsulation: 2 });
__decorate([
    HostBinding('class.k-floating-label-container'),
    __metadata("design:type", Boolean)
], FloatingLabelComponent.prototype, "hostClasses", void 0);
__decorate([
    HostBinding('class.k-state-focused'),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], FloatingLabelComponent.prototype, "focusedClass", null);
__decorate([
    HostBinding('class.k-state-invalid'),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], FloatingLabelComponent.prototype, "invalidClass", null);
__decorate([
    HostBinding('attr.dir'),
    __metadata("design:type", String)
], FloatingLabelComponent.prototype, "direction", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], FloatingLabelComponent.prototype, "labelCssStyle", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], FloatingLabelComponent.prototype, "labelCssClass", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], FloatingLabelComponent.prototype, "id", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], FloatingLabelComponent.prototype, "text", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], FloatingLabelComponent.prototype, "optional", void 0);
__decorate([
    Output(),
    __metadata("design:type", EventEmitter)
], FloatingLabelComponent.prototype, "positionChange", void 0);
__decorate([
    ContentChild(KendoInput, { static: false }),
    __metadata("design:type", Object)
], FloatingLabelComponent.prototype, "kendoInput", void 0);
__decorate([
    ContentChild(NgControl, { static: false }),
    __metadata("design:type", NgControl)
], FloatingLabelComponent.prototype, "formControl", void 0);
FloatingLabelComponent = __decorate([ __metadata("design:paramtypes", [ElementRef,
        Renderer2,
        ChangeDetectorRef,
        LocalizationService])
], FloatingLabelComponent);

const COMPONENT_DIRECTIVES = [FloatingLabelComponent];
/**
 * Represents the [NgModule]({{ site.data.urls.angular['ngmoduleapi'] }})
 * definition for the TextBox directive.
 *
 * @example
 *
 * ```ts-no-run
 *
 * // The browser platform with a compiler
 * import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
 *
 * import { NgModule } from '@angular/core';
 *
 * // Import the app component
 * import { AppComponent } from './app.component';
 *
 * // Define the app module
 * _@NgModule({
 *     declarations: [AppComponent], // declare app component
 *     imports:      [BrowserModule, FloatingLabelModule], // import FloatingLabel module
 *     bootstrap:    [AppComponent]
 * })
 * export class AppModule {}
 *
 * // Compile and launch the module
 * platformBrowserDynamic().bootstrapModule(AppModule);
 *
 * ```
 */
let FloatingLabelModule = class FloatingLabelModule {
};
FloatingLabelModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: FloatingLabelModule });
FloatingLabelModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function FloatingLabelModule_Factory(t) { return new (t || FloatingLabelModule)(); }, imports: [[CommonModule, SharedDirectivesModule], SharedDirectivesModule] });

/**
 * Represents the [Kendo UI Label component for Angular]({% slug label_basic %}).
 *
 * Associates a label with input elements or components.
 *
 * @example
 * ```ts
 *
 * _@Component({
 *   selector: 'my-app',
 *   template: `
 *     <kendo-label [for]="input" text="First name">
 *       <input [(ngModel)]="name" kendoTextBox #input />
 *     </kendo-label>
 *   `
 * })
 * class AppComponent {
 *     public name = 'John';
 * }
 *
 * ```
 */
let LabelComponent = class LabelComponent {
    constructor(elementRef, renderer, localization) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.localization = localization;
        this.subscriptions = new Subscription();
        validatePackage(packageMetadata);
        this.direction = localization.rtl ? 'rtl' : 'ltr';
        this.renderer.removeAttribute(this.elementRef.nativeElement, 'id');
    }
    /**
     * @hidden
     */
    ngAfterContentInit() {
        if (this.for) {
            this.control = this.for;
            return;
        }
        const wrappedNativeInput = getWrappedNativeInput(this.elementRef.nativeElement);
        if (wrappedNativeInput) {
            if (!wrappedNativeInput.hasAttribute('id')) {
                this.renderer.setAttribute(wrappedNativeInput, 'id', `k-${guid()}`);
            }
            this.control = wrappedNativeInput;
            return;
        }
        this.control = this.kendoInput;
    }
    /**
     * @hidden
     */
    ngOnInit() {
        this.subscriptions.add(this.localization.changes.subscribe(({ rtl }) => {
            this.direction = rtl ? 'rtl' : 'ltr';
        }));
    }
    /**
     * @hidden
     */
    ngAfterViewInit() {
        this.labelDirective.setAriaLabelledby();
    }
    /**
     * @hidden
     */
    ngOnDestroy() {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    }
    /**
     * @hidden
     */
    textFor(key) {
        return this.localization.get(key);
    }
};
LabelComponent.ɵfac = function LabelComponent_Factory(t) { return new (t || LabelComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.Renderer2), ɵngcc0.ɵɵdirectiveInject(ɵngcc1.LocalizationService)); };
LabelComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: LabelComponent, selectors: [["kendo-label"]], contentQueries: function LabelComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        ɵngcc0.ɵɵstaticContentQuery(dirIndex, KendoInput, true);
    } if (rf & 2) {
        var _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.kendoInput = _t.first);
    } }, viewQuery: function LabelComponent_Query(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵstaticViewQuery(LabelDirective, true);
    } if (rf & 2) {
        var _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.labelDirective = _t.first);
    } }, hostVars: 1, hostBindings: function LabelComponent_HostBindings(rf, ctx) { if (rf & 2) {
        ɵngcc0.ɵɵattribute("dir", ctx.direction);
    } }, inputs: { text: "text", for: "for", optional: "optional" }, exportAs: ["kendoLabel"], features: [ɵngcc0.ɵɵProvidersFeature([
            LocalizationService,
            {
                provide: L10N_PREFIX,
                useValue: 'kendo.label'
            }
        ])], ngContentSelectors: _c3, decls: 6, vars: 5, consts: [["kendoLabelLocalizedMessages", "", 6, "optional"], [3, "for"], ["class", "k-label-optional", 4, "ngIf"], [1, "k-label-optional"]], template: function LabelComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵprojectionDef();
        ɵngcc0.ɵɵelementContainerStart(0, 0);
        ɵngcc0.ɵɵi18nAttributes(1, _c6);
        ɵngcc0.ɵɵelementContainerEnd();
        ɵngcc0.ɵɵelementStart(2, "label", 1);
        ɵngcc0.ɵɵtext(3);
        ɵngcc0.ɵɵtemplate(4, LabelComponent_span_4_Template, 2, 1, "span", 2);
        ɵngcc0.ɵɵelementEnd();
        ɵngcc0.ɵɵprojection(5);
    } if (rf & 2) {
        ɵngcc0.ɵɵadvance(2);
        ɵngcc0.ɵɵclassProp("k-label-empty", !ctx.text);
        ɵngcc0.ɵɵproperty("for", ctx.control);
        ɵngcc0.ɵɵadvance(1);
        ɵngcc0.ɵɵtextInterpolate1(" ", ctx.text, "");
        ɵngcc0.ɵɵadvance(1);
        ɵngcc0.ɵɵproperty("ngIf", ctx.optional);
    } }, directives: [LocalizedMessagesDirective, LabelDirective, ɵngcc2.NgIf], encapsulation: 2 });
__decorate([
    HostBinding('attr.dir'),
    __metadata("design:type", String)
], LabelComponent.prototype, "direction", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], LabelComponent.prototype, "text", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], LabelComponent.prototype, "for", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], LabelComponent.prototype, "optional", void 0);
__decorate([
    ViewChild(LabelDirective, { static: true }),
    __metadata("design:type", LabelDirective)
], LabelComponent.prototype, "labelDirective", void 0);
__decorate([
    ContentChild(KendoInput, { static: true }),
    __metadata("design:type", Object)
], LabelComponent.prototype, "kendoInput", void 0);
LabelComponent = __decorate([ __metadata("design:paramtypes", [ElementRef,
        Renderer2,
        LocalizationService])
], LabelComponent);

const COMPONENT_DIRECTIVES$1 = [
    LabelDirective,
    LabelComponent
];
/**
 * The exported package module.
 *
 * The package exports:
 * - `LabelDirective`&mdash;The Label directive class.
 * - `LabelComponent`&mdash;The Label component class
 * - `FLoatingLabel`&mdash;The FloatingLabel component class.
 *
 * @example
 *
 * ```ts-no-run
 * // Import the Label module
 * import { LabelModule } from '@progress/kendo-angular-label';
 *
 * // The browser platform with a compiler
 * import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
 *
 * import { NgModule } from '@angular/core';
 *
 * // Import the app component
 * import { AppComponent } from './app.component';
 *
 * // Define the app module
 * _@NgModule({
 *     declarations: [AppComponent], // declare app component
 *     imports:      [BrowserModule, LabelModule], // import Label module
 *     bootstrap:    [AppComponent]
 * })
 * export class AppModule {}
 *
 * // Compile and launch the module
 * platformBrowserDynamic().bootstrapModule(AppModule);
 *
 * ```
 */
let LabelModule = class LabelModule {
};
LabelModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: LabelModule });
LabelModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function LabelModule_Factory(t) { return new (t || LabelModule)(); }, imports: [[CommonModule, SharedDirectivesModule], FloatingLabelModule, SharedDirectivesModule] });
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(LabelDirective, [{
        type: Directive,
        args: [{
                selector: 'label[for]' //tslint:disable-line:directive-selector
            }]
    }], function () { return [{ type: ɵngcc0.ElementRef }, { type: ɵngcc0.Renderer2 }, { type: ɵngcc0.NgZone }]; }, { labelClass: [{
            type: HostBinding,
            args: ['class.k-label']
        }], labelFor: [{
            type: HostBinding,
            args: ['attr.for']
        }], for: [{
            type: Input
        }] }); })();
const ɵMessages_BaseFactory = /*@__PURE__*/ ɵngcc0.ɵɵgetInheritedFactory(Messages);
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(LocalizedMessagesDirective, [{
        type: Directive,
        args: [{
                providers: [
                    {
                        provide: Messages,
                        useExisting: forwardRef(() => LocalizedMessagesDirective_1)
                    }
                ],
                selector: `
      [kendoLabelLocalizedMessages],
      [kendoFloatingLabelLocalizedMessages]
    `
            }]
    }], function () { return [{ type: ɵngcc1.LocalizationService }]; }, null); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(CustomMessagesComponent, [{
        type: Component,
        args: [{
                providers: [
                    {
                        provide: Messages,
                        useExisting: forwardRef(() => CustomMessagesComponent_1)
                    }
                ],
                selector: 'kendo-label-messages, kendo-floatinglabel-messages',
                template: ``
            }]
    }], function () { return [{ type: ɵngcc1.LocalizationService }]; }, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(SharedDirectivesModule, { declarations: [LocalizedMessagesDirective, CustomMessagesComponent], exports: [LocalizedMessagesDirective, CustomMessagesComponent] }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(SharedDirectivesModule, [{
        type: NgModule,
        args: [{
                declarations: [SHARED_DIRECTIVES],
                exports: [SHARED_DIRECTIVES]
            }]
    }], null, null); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(FloatingLabelComponent, [{
        type: Component,
        args: [{
                selector: 'kendo-floatinglabel',
                exportAs: 'kendoFloatingLabel',
                providers: [
                    LocalizationService,
                    {
                        provide: L10N_PREFIX,
                        useValue: 'kendo.floatinglabel'
                    }
                ],
                template: `
        <ng-container kendoFloatingLabelLocalizedMessages
            i18n-optional="kendo.floatinglabel.optional|The text for the optional segment of a FloatingLabel component"
            optional="Optional"
         >
        </ng-container>
        <ng-content></ng-content>
        <label *ngIf="text" [ngClass]="labelCssClass" [ngStyle]="labelCssStyle" [for]="id" [attr.id]="labelId" class="k-label">
            {{ text }}<span *ngIf="optional" class="k-label-optional">({{textFor('optional')}})</span>
        </label>
    `
            }]
    }], function () { return [{ type: ɵngcc0.ElementRef }, { type: ɵngcc0.Renderer2 }, { type: ɵngcc0.ChangeDetectorRef }, { type: ɵngcc1.LocalizationService }]; }, { hostClasses: [{
            type: HostBinding,
            args: ['class.k-floating-label-container']
        }], positionChange: [{
            type: Output
        }], direction: [{
            type: HostBinding,
            args: ['attr.dir']
        }], focusedClass: [{
            type: HostBinding,
            args: ['class.k-state-focused']
        }], invalidClass: [{
            type: HostBinding,
            args: ['class.k-state-invalid']
        }], id: [{
            type: Input
        }], labelCssStyle: [{
            type: Input
        }], labelCssClass: [{
            type: Input
        }], text: [{
            type: Input
        }], optional: [{
            type: Input
        }], kendoInput: [{
            type: ContentChild,
            args: [KendoInput, { static: false }]
        }], formControl: [{
            type: ContentChild,
            args: [NgControl, { static: false }]
        }] }); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(FloatingLabelModule, { declarations: function () { return [FloatingLabelComponent]; }, imports: function () { return [CommonModule, SharedDirectivesModule]; }, exports: function () { return [FloatingLabelComponent, SharedDirectivesModule]; } }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(FloatingLabelModule, [{
        type: NgModule,
        args: [{
                declarations: [...COMPONENT_DIRECTIVES],
                exports: [...COMPONENT_DIRECTIVES, SharedDirectivesModule],
                imports: [CommonModule, SharedDirectivesModule]
            }]
    }], null, null); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(LabelComponent, [{
        type: Component,
        args: [{
                selector: 'kendo-label',
                exportAs: 'kendoLabel',
                providers: [
                    LocalizationService,
                    {
                        provide: L10N_PREFIX,
                        useValue: 'kendo.label'
                    }
                ],
                template: `
        <ng-container kendoLabelLocalizedMessages
            i18n-optional="kendo.label.optional|The text for the optional segment of a Label component"
            optional="Optional"
         >
        </ng-container>
        <label
            [for]="control"
            [class.k-label-empty]="!text">
            {{ text }}<span *ngIf="optional" class="k-label-optional">({{textFor('optional')}})</span>
        </label>
        <ng-content></ng-content>
    `
            }]
    }], function () { return [{ type: ɵngcc0.ElementRef }, { type: ɵngcc0.Renderer2 }, { type: ɵngcc1.LocalizationService }]; }, { direction: [{
            type: HostBinding,
            args: ['attr.dir']
        }], text: [{
            type: Input
        }], for: [{
            type: Input
        }], optional: [{
            type: Input
        }], labelDirective: [{
            type: ViewChild,
            args: [LabelDirective, { static: true }]
        }], kendoInput: [{
            type: ContentChild,
            args: [KendoInput, { static: true }]
        }] }); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(LabelModule, { declarations: function () { return [LabelDirective, LabelComponent]; }, imports: function () { return [CommonModule, SharedDirectivesModule]; }, exports: function () { return [LabelDirective, LabelComponent, FloatingLabelModule, SharedDirectivesModule]; } }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(LabelModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, SharedDirectivesModule],
                declarations: [...COMPONENT_DIRECTIVES$1],
                exports: [...COMPONENT_DIRECTIVES$1, FloatingLabelModule, SharedDirectivesModule]
            }]
    }], null, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { LocalizedMessagesDirective, Messages, SharedDirectivesModule, LabelDirective, LabelModule, FloatingLabelModule, FloatingLabelComponent, LabelComponent, CustomMessagesComponent };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiaW5kZXguanMiLCJzb3VyY2VzIjpbImluZGV4LmpzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBOzs7OzttQ0FBRTtBQUNGO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0EsNkJBSUc7QUFDSDtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTs7OElBQUM7QUFDRDtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7Ozs7Ozs7a0RBQUU7QUFDRix3RUFhRztBQUNIO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBOzs7Ozs7O3lKQUFFO0FBQ0Ysa0VBV0c7QUFDSDtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBOzsrSkFNMkI7QUFDM0I7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7b0hBQUU7QUFDRjtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0EscUNBdUJHO0FBQ0g7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7O2lPQU93QjtBQUN4QjtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztvR0FBRTtBQUNGO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBLDZCQXlCRztBQUNIO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0E7QUFDQTs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OzBCQU9nQjtBQUNoQjtBQUNBO0FBQ0E7QUFDQTtBQUNBO0FBQ0EiLCJzb3VyY2VzQ29udGVudCI6WyIvKiotLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLVxuKiBDb3B5cmlnaHQgwqkgMjAyMSBQcm9ncmVzcyBTb2Z0d2FyZSBDb3Jwb3JhdGlvbi4gQWxsIHJpZ2h0cyByZXNlcnZlZC5cbiogTGljZW5zZWQgdW5kZXIgY29tbWVyY2lhbCBsaWNlbnNlLiBTZWUgTElDRU5TRS5tZCBpbiB0aGUgcHJvamVjdCByb290IGZvciBtb3JlIGluZm9ybWF0aW9uXG4qLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLSovXG5pbXBvcnQgeyBfX2RlY29yYXRlLCBfX21ldGFkYXRhIH0gZnJvbSAndHNsaWInO1xuaW1wb3J0IHsgSW5wdXQsIEhvc3RCaW5kaW5nLCBEaXJlY3RpdmUsIEVsZW1lbnRSZWYsIFJlbmRlcmVyMiwgTmdab25lLCBmb3J3YXJkUmVmLCBDb21wb25lbnQsIE5nTW9kdWxlLCBFdmVudEVtaXR0ZXIsIGlzRGV2TW9kZSwgT3V0cHV0LCBDb250ZW50Q2hpbGQsIENoYW5nZURldGVjdG9yUmVmLCBWaWV3Q2hpbGQgfSBmcm9tICdAYW5ndWxhci9jb3JlJztcbmltcG9ydCB7IGlzRG9jdW1lbnRBdmFpbGFibGUsIGd1aWQsIGhhc09ic2VydmVycywgS2VuZG9JbnB1dCB9IGZyb20gJ0Bwcm9ncmVzcy9rZW5kby1hbmd1bGFyLWNvbW1vbic7XG5pbXBvcnQgeyBDb21wb25lbnRNZXNzYWdlcywgTG9jYWxpemF0aW9uU2VydmljZSwgTDEwTl9QUkVGSVggfSBmcm9tICdAcHJvZ3Jlc3Mva2VuZG8tYW5ndWxhci1sMTBuJztcbmltcG9ydCB7IENvbW1vbk1vZHVsZSB9IGZyb20gJ0Bhbmd1bGFyL2NvbW1vbic7XG5pbXBvcnQgeyBOZ0NvbnRyb2wgfSBmcm9tICdAYW5ndWxhci9mb3Jtcyc7XG5pbXBvcnQgeyB2YWxpZGF0ZVBhY2thZ2UgfSBmcm9tICdAcHJvZ3Jlc3Mva2VuZG8tbGljZW5zaW5nJztcbmltcG9ydCB7IE9ic2VydmFibGUsIFN1YnNjcmlwdGlvbiB9IGZyb20gJ3J4anMnO1xuXG4vKipcbiAqIEBoaWRkZW5cbiAqL1xuY29uc3QgaXNVcGxvYWRDb21wb25lbnQgPSAoY29tcG9uZW50KSA9PiBjb21wb25lbnQud3JhcHBlciAmJiAoY29tcG9uZW50LndyYXBwZXIudGFnTmFtZSA9PT0gJ0tFTkRPLVVQTE9BRCcgfHwgY29tcG9uZW50LndyYXBwZXIudGFnTmFtZSA9PT0gJ0tFTkRPLUZJTEVTRUxFQ1QnKTtcbi8qKlxuICogQGhpZGRlblxuICovXG5jb25zdCBpc0lucHV0RWxlbWVudCA9IChjb21wb25lbnQpID0+IGNvbXBvbmVudCBpbnN0YW5jZW9mIEhUTUxFbGVtZW50O1xuLyoqXG4gKiBAaGlkZGVuXG4gKi9cbmNvbnN0IGlucHV0RWxlbWVudEhhc0F0dHIgPSAoZWxlbWVudCwgYXR0cmlidXRlKSA9PiBlbGVtZW50Lmhhc0F0dHJpYnV0ZShhdHRyaWJ1dGUpO1xuLyoqXG4gKiBAaGlkZGVuXG4gKi9cbmNvbnN0IGdldFdyYXBwZWROYXRpdmVJbnB1dCA9IChlbGVtZW50KSA9PiBlbGVtZW50LnF1ZXJ5U2VsZWN0b3IoJ2tlbmRvLWxhYmVsID4gaW5wdXQsIGtlbmRvLWxhYmVsID4gdGV4dGFyZWEsIGtlbmRvLWxhYmVsID4gc2VsZWN0Jyk7XG4vKipcbiAqIEBoaWRkZW5cbiAqL1xuY29uc3QgZ2V0Um9vdEVsZW1lbnQgPSAoZWxlbWVudCkgPT4ge1xuICAgIGlmICghZWxlbWVudCkge1xuICAgICAgICByZXR1cm4gbnVsbDtcbiAgICB9XG4gICAgbGV0IHJvb3RFbGVtZW50ID0gZWxlbWVudDtcbiAgICB3aGlsZSAocm9vdEVsZW1lbnQucGFyZW50RWxlbWVudCkge1xuICAgICAgICByb290RWxlbWVudCA9IHJvb3RFbGVtZW50LnBhcmVudEVsZW1lbnQ7XG4gICAgfVxuICAgIHJldHVybiByb290RWxlbWVudDtcbn07XG5cbi8qKlxuICogUmVwcmVzZW50cyB0aGUgW0tlbmRvIFVJIExhYmVsIGRpcmVjdGl2ZSBmb3IgQW5ndWxhcl0oeyUgc2x1ZyBvdmVydmlld19sYWJlbCAlfSkuXG4gKiBUaGUgTGFiZWwgYXNzb2NpYXRlcyBhIGZvY3VzYWJsZSBBbmd1bGFyIGNvbXBvbmVudCBvciBhbiBIVE1MIGVsZW1lbnRcbiAqIHdpdGggYSBgbGFiZWxgIHRhZyBieSB1c2luZyB0aGUgYFtmb3JdYCBwcm9wZXJ0eSBiaW5kaW5nLlxuICpcbiAqIFRvIGFzc29jaWF0ZSBhIGNvbXBvbmVudCBieSB1c2luZyB0aGUgYGxhYmVsYCBlbGVtZW50LCBlaXRoZXI6XG4gKiAqIFNldCB0aGUgYFtmb3JdYCBwcm9wZXJ0eSBiaW5kaW5nIHRvIGFcbiAqIFt0ZW1wbGF0ZSByZWZlcmVuY2UgdmFyaWFibGVdKHt7IHNpdGUuZGF0YS51cmxzLmFuZ3VsYXJbJ3RlbXBsYXRlc3ludGF4J10gfX0jdGVtcGxhdGUtcmVmZXJlbmNlLXZhcmlhYmxlcy0tdmFyLSksIG9yXG4gKiAqIFNldCB0aGUgYFtmb3JdYCBwcm9wZXJ0eSBiaW5kaW5nIHRvIGFuIGBpZGAgSFRNTCBzdHJpbmcgdmFsdWUuXG4gKlxuICogQGV4YW1wbGVcbiAqIGBgYHRzXG4gKiBfQENvbXBvbmVudCh7XG4gKiBzZWxlY3RvcjogJ215LWFwcCcsXG4gKiB0ZW1wbGF0ZTogYFxuICogIDxkaXYgY2xhc3M9XCJyb3cgZXhhbXBsZS13cmFwcGVyXCIgc3R5bGU9XCJtaW4taGVpZ2h0OiA0NTBweDtcIj5cbiAqICAgIDxkaXYgY2xhc3M9XCJjb2wteHMtMTIgY29sLW1kLTYgZXhhbXBsZS1jb2xcIj5cbiAqICAgICAgPGxhYmVsIFtmb3JdPVwiZGF0ZXBpY2tlclwiPkRhdGVQaWNrZXI6IDwvbGFiZWw+XG4gKiAgICAgIDxrZW5kby1kYXRlcGlja2VyICNkYXRlcGlja2VyPjwva2VuZG8tZGF0ZXBpY2tlcj5cbiAqICAgIDwvZGl2PlxuICpcbiAqICAgIDxkaXYgY2xhc3M9XCJjb2wteHMtMTIgY29sLW1kLTYgZXhhbXBsZS1jb2xcIj5cbiAqICAgICAgPGxhYmVsIGZvcj1cImlucHV0XCI+SW5wdXQ6IDwvbGFiZWw+XG4gKiAgICAgIDxpbnB1dCBpZD1cImlucHV0XCIgLz5cbiAqICAgIDwvZGl2PlxuICogIDwvZGl2PlxuICogYFxuICogfSlcbiAqIGNsYXNzIEFwcENvbXBvbmVudCB7IH1cbiAqIGBgYFxuICovXG5sZXQgTGFiZWxEaXJlY3RpdmUgPSBjbGFzcyBMYWJlbERpcmVjdGl2ZSB7XG4gICAgY29uc3RydWN0b3IobGFiZWwsIHJlbmRlcmVyLCB6b25lKSB7XG4gICAgICAgIHRoaXMubGFiZWwgPSBsYWJlbDtcbiAgICAgICAgdGhpcy5yZW5kZXJlciA9IHJlbmRlcmVyO1xuICAgICAgICB0aGlzLnpvbmUgPSB6b25lO1xuICAgICAgICB0aGlzLmxhYmVsQ2xhc3MgPSB0cnVlO1xuICAgICAgICB0aGlzLmhhbmRsZUNsaWNrID0gKCkgPT4ge1xuICAgICAgICAgICAgY29uc3QgY29tcG9uZW50ID0gdGhpcy5nZXRGb2N1c2FibGVDb21wb25lbnQoKTtcbiAgICAgICAgICAgIGlmICghY29tcG9uZW50KSB7XG4gICAgICAgICAgICAgICAgcmV0dXJuO1xuICAgICAgICAgICAgfVxuICAgICAgICAgICAgaWYgKGlzVXBsb2FkQ29tcG9uZW50KGNvbXBvbmVudCkpIHtcbiAgICAgICAgICAgICAgICBjb21wb25lbnQuZmlsZVNlbGVjdC5uYXRpdmVFbGVtZW50LmNsaWNrKCk7XG4gICAgICAgICAgICB9XG4gICAgICAgICAgICBpZiAoY29tcG9uZW50LmZvY3VzKSB7XG4gICAgICAgICAgICAgICAgY29tcG9uZW50LmZvY3VzKCk7XG4gICAgICAgICAgICB9XG4gICAgICAgIH07XG4gICAgfVxuICAgIGdldCBsYWJlbEZvcigpIHtcbiAgICAgICAgaWYgKHR5cGVvZiB0aGlzLmZvciA9PT0gJ3N0cmluZycpIHtcbiAgICAgICAgICAgIHJldHVybiB0aGlzLmZvcjtcbiAgICAgICAgfVxuICAgICAgICBpZiAoIWlzRG9jdW1lbnRBdmFpbGFibGUoKSkge1xuICAgICAgICAgICAgcmV0dXJuIG51bGw7XG4gICAgICAgIH1cbiAgICAgICAgY29uc3QgY29tcG9uZW50ID0gdGhpcy5nZXRGb2N1c2FibGVDb21wb25lbnQoKSB8fCB7fTtcbiAgICAgICAgaWYgKGlzSW5wdXRFbGVtZW50KGNvbXBvbmVudCkgJiYgIWlucHV0RWxlbWVudEhhc0F0dHIoY29tcG9uZW50LCAnaWQnKSkge1xuICAgICAgICAgICAgdGhpcy5yZW5kZXJlci5zZXRBdHRyaWJ1dGUoY29tcG9uZW50LCAnaWQnLCBgay0ke2d1aWQoKX1gKTtcbiAgICAgICAgfVxuICAgICAgICByZXR1cm4gY29tcG9uZW50LmZvY3VzYWJsZUlkIHx8IGNvbXBvbmVudC5pZCB8fCBudWxsO1xuICAgIH1cbiAgICAvKipcbiAgICAgKiBAaGlkZGVuXG4gICAgICovXG4gICAgbmdBZnRlclZpZXdJbml0KCkge1xuICAgICAgICB0aGlzLnNldEFyaWFMYWJlbGxlZGJ5KCk7XG4gICAgICAgIHRoaXMuem9uZS5ydW5PdXRzaWRlQW5ndWxhcigoKSA9PiB0aGlzLmNsaWNrTGlzdGVuZXIgPSB0aGlzLnJlbmRlcmVyLmxpc3Rlbih0aGlzLmxhYmVsLm5hdGl2ZUVsZW1lbnQsICdjbGljaycsIHRoaXMuaGFuZGxlQ2xpY2spKTtcbiAgICB9XG4gICAgLyoqXG4gICAgICogQGhpZGRlblxuICAgICAqL1xuICAgIG5nT25EZXN0cm95KCkge1xuICAgICAgICBpZiAodGhpcy5jbGlja0xpc3RlbmVyKSB7XG4gICAgICAgICAgICB0aGlzLmNsaWNrTGlzdGVuZXIoKTtcbiAgICAgICAgfVxuICAgIH1cbiAgICAvKipcbiAgICAgKiBAaGlkZGVuXG4gICAgICovXG4gICAgc2V0QXJpYUxhYmVsbGVkYnkoKSB7XG4gICAgICAgIGlmICghaXNEb2N1bWVudEF2YWlsYWJsZSgpKSB7XG4gICAgICAgICAgICByZXR1cm47XG4gICAgICAgIH1cbiAgICAgICAgY29uc3QgY29tcG9uZW50ID0gdGhpcy5nZXRGb2N1c2FibGVDb21wb25lbnQoKTtcbiAgICAgICAgaWYgKGNvbXBvbmVudCAmJiBjb21wb25lbnQuZm9jdXNhYmxlSWQpIHtcbiAgICAgICAgICAgIGNvbnN0IHJvb3RFbGVtZW50ID0gZ2V0Um9vdEVsZW1lbnQodGhpcy5sYWJlbC5uYXRpdmVFbGVtZW50KTtcbiAgICAgICAgICAgIGNvbnN0IGxhYmVsVGFyZ2V0ID0gcm9vdEVsZW1lbnQucXVlcnlTZWxlY3RvcihgIyR7Y29tcG9uZW50LmZvY3VzYWJsZUlkfWApO1xuICAgICAgICAgICAgaWYgKCFsYWJlbFRhcmdldCkge1xuICAgICAgICAgICAgICAgIHJldHVybjtcbiAgICAgICAgICAgIH1cbiAgICAgICAgICAgIGNvbnN0IGxhYmVsRWxlbWVudCA9IHRoaXMubGFiZWwubmF0aXZlRWxlbWVudDtcbiAgICAgICAgICAgIGNvbnN0IGlkID0gbGFiZWxFbGVtZW50LmlkIHx8IGBrLSR7Z3VpZCgpfWA7XG4gICAgICAgICAgICBpZiAoIWxhYmVsRWxlbWVudC5nZXRBdHRyaWJ1dGUoJ2lkJykpIHtcbiAgICAgICAgICAgICAgICB0aGlzLnJlbmRlcmVyLnNldEF0dHJpYnV0ZShsYWJlbEVsZW1lbnQsICdpZCcsIGlkKTtcbiAgICAgICAgICAgIH1cbiAgICAgICAgICAgIHRoaXMucmVuZGVyZXIuc2V0QXR0cmlidXRlKGxhYmVsVGFyZ2V0LCAnYXJpYS1sYWJlbGxlZGJ5JywgaWQpO1xuICAgICAgICB9XG4gICAgfVxuICAgIGdldEZvY3VzYWJsZUNvbXBvbmVudCgpIHtcbiAgICAgICAgY29uc3QgdGFyZ2V0ID0gdGhpcy5mb3I7XG4gICAgICAgIHJldHVybiB0YXJnZXQgJiYgdGFyZ2V0LmZvY3VzICE9PSB1bmRlZmluZWQgPyB0YXJnZXQgOiBudWxsO1xuICAgIH1cbn07XG5fX2RlY29yYXRlKFtcbiAgICBJbnB1dCgpLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246dHlwZVwiLCBPYmplY3QpXG5dLCBMYWJlbERpcmVjdGl2ZS5wcm90b3R5cGUsIFwiZm9yXCIsIHZvaWQgMCk7XG5fX2RlY29yYXRlKFtcbiAgICBIb3N0QmluZGluZygnYXR0ci5mb3InKSxcbiAgICBfX21ldGFkYXRhKFwiZGVzaWduOnR5cGVcIiwgU3RyaW5nKSxcbiAgICBfX21ldGFkYXRhKFwiZGVzaWduOnBhcmFtdHlwZXNcIiwgW10pXG5dLCBMYWJlbERpcmVjdGl2ZS5wcm90b3R5cGUsIFwibGFiZWxGb3JcIiwgbnVsbCk7XG5fX2RlY29yYXRlKFtcbiAgICBIb3N0QmluZGluZygnY2xhc3Muay1sYWJlbCcpLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246dHlwZVwiLCBCb29sZWFuKVxuXSwgTGFiZWxEaXJlY3RpdmUucHJvdG90eXBlLCBcImxhYmVsQ2xhc3NcIiwgdm9pZCAwKTtcbkxhYmVsRGlyZWN0aXZlID0gX19kZWNvcmF0ZShbXG4gICAgRGlyZWN0aXZlKHtcbiAgICAgICAgc2VsZWN0b3I6ICdsYWJlbFtmb3JdJyAvL3RzbGludDpkaXNhYmxlLWxpbmU6ZGlyZWN0aXZlLXNlbGVjdG9yXG4gICAgfSksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjpwYXJhbXR5cGVzXCIsIFtFbGVtZW50UmVmLCBSZW5kZXJlcjIsIE5nWm9uZV0pXG5dLCBMYWJlbERpcmVjdGl2ZSk7XG5cbi8qKlxuICogQGhpZGRlblxuICovXG5jbGFzcyBNZXNzYWdlcyBleHRlbmRzIENvbXBvbmVudE1lc3NhZ2VzIHtcbn1cbl9fZGVjb3JhdGUoW1xuICAgIElucHV0KCksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIFN0cmluZylcbl0sIE1lc3NhZ2VzLnByb3RvdHlwZSwgXCJvcHRpb25hbFwiLCB2b2lkIDApO1xuXG52YXIgTG9jYWxpemVkTWVzc2FnZXNEaXJlY3RpdmVfMTtcbi8qKlxuICogQGhpZGRlblxuICovXG5sZXQgTG9jYWxpemVkTWVzc2FnZXNEaXJlY3RpdmUgPSBMb2NhbGl6ZWRNZXNzYWdlc0RpcmVjdGl2ZV8xID0gY2xhc3MgTG9jYWxpemVkTWVzc2FnZXNEaXJlY3RpdmUgZXh0ZW5kcyBNZXNzYWdlcyB7XG4gICAgY29uc3RydWN0b3Ioc2VydmljZSkge1xuICAgICAgICBzdXBlcigpO1xuICAgICAgICB0aGlzLnNlcnZpY2UgPSBzZXJ2aWNlO1xuICAgIH1cbn07XG5Mb2NhbGl6ZWRNZXNzYWdlc0RpcmVjdGl2ZSA9IExvY2FsaXplZE1lc3NhZ2VzRGlyZWN0aXZlXzEgPSBfX2RlY29yYXRlKFtcbiAgICBEaXJlY3RpdmUoe1xuICAgICAgICBwcm92aWRlcnM6IFtcbiAgICAgICAgICAgIHtcbiAgICAgICAgICAgICAgICBwcm92aWRlOiBNZXNzYWdlcyxcbiAgICAgICAgICAgICAgICB1c2VFeGlzdGluZzogZm9yd2FyZFJlZigoKSA9PiBMb2NhbGl6ZWRNZXNzYWdlc0RpcmVjdGl2ZV8xKVxuICAgICAgICAgICAgfVxuICAgICAgICBdLFxuICAgICAgICBzZWxlY3RvcjogYFxuICAgICAgW2tlbmRvTGFiZWxMb2NhbGl6ZWRNZXNzYWdlc10sXG4gICAgICBba2VuZG9GbG9hdGluZ0xhYmVsTG9jYWxpemVkTWVzc2FnZXNdXG4gICAgYFxuICAgIH0pLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246cGFyYW10eXBlc1wiLCBbTG9jYWxpemF0aW9uU2VydmljZV0pXG5dLCBMb2NhbGl6ZWRNZXNzYWdlc0RpcmVjdGl2ZSk7XG5cbnZhciBDdXN0b21NZXNzYWdlc0NvbXBvbmVudF8xO1xuLyoqXG4gKiBDdXN0b20gY29tcG9uZW50IG1lc3NhZ2VzIG92ZXJyaWRlIGRlZmF1bHQgY29tcG9uZW50IG1lc3NhZ2VzXG4gKiAoW3NlZSBleGFtcGxlXSh7JSBzbHVnIGxhYmVsX2dsb2JhbGl6YXRpb24gJX0jdG9jLWxvY2FsaXphdGlvbikpLlxuICovXG5sZXQgQ3VzdG9tTWVzc2FnZXNDb21wb25lbnQgPSBDdXN0b21NZXNzYWdlc0NvbXBvbmVudF8xID0gY2xhc3MgQ3VzdG9tTWVzc2FnZXNDb21wb25lbnQgZXh0ZW5kcyBNZXNzYWdlcyB7XG4gICAgY29uc3RydWN0b3Ioc2VydmljZSkge1xuICAgICAgICBzdXBlcigpO1xuICAgICAgICB0aGlzLnNlcnZpY2UgPSBzZXJ2aWNlO1xuICAgIH1cbiAgICBnZXQgb3ZlcnJpZGUoKSB7XG4gICAgICAgIHJldHVybiB0cnVlO1xuICAgIH1cbn07XG5DdXN0b21NZXNzYWdlc0NvbXBvbmVudCA9IEN1c3RvbU1lc3NhZ2VzQ29tcG9uZW50XzEgPSBfX2RlY29yYXRlKFtcbiAgICBDb21wb25lbnQoe1xuICAgICAgICBwcm92aWRlcnM6IFtcbiAgICAgICAgICAgIHtcbiAgICAgICAgICAgICAgICBwcm92aWRlOiBNZXNzYWdlcyxcbiAgICAgICAgICAgICAgICB1c2VFeGlzdGluZzogZm9yd2FyZFJlZigoKSA9PiBDdXN0b21NZXNzYWdlc0NvbXBvbmVudF8xKVxuICAgICAgICAgICAgfVxuICAgICAgICBdLFxuICAgICAgICBzZWxlY3RvcjogJ2tlbmRvLWxhYmVsLW1lc3NhZ2VzLCBrZW5kby1mbG9hdGluZ2xhYmVsLW1lc3NhZ2VzJyxcbiAgICAgICAgdGVtcGxhdGU6IGBgXG4gICAgfSksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjpwYXJhbXR5cGVzXCIsIFtMb2NhbGl6YXRpb25TZXJ2aWNlXSlcbl0sIEN1c3RvbU1lc3NhZ2VzQ29tcG9uZW50KTtcblxuY29uc3QgU0hBUkVEX0RJUkVDVElWRVMgPSBbXG4gICAgTG9jYWxpemVkTWVzc2FnZXNEaXJlY3RpdmUsXG4gICAgQ3VzdG9tTWVzc2FnZXNDb21wb25lbnRcbl07XG4vKipcbiAqIEBoaWRkZW5cbiAqL1xubGV0IFNoYXJlZERpcmVjdGl2ZXNNb2R1bGUgPSBjbGFzcyBTaGFyZWREaXJlY3RpdmVzTW9kdWxlIHtcbn07XG5TaGFyZWREaXJlY3RpdmVzTW9kdWxlID0gX19kZWNvcmF0ZShbXG4gICAgTmdNb2R1bGUoe1xuICAgICAgICBkZWNsYXJhdGlvbnM6IFtTSEFSRURfRElSRUNUSVZFU10sXG4gICAgICAgIGV4cG9ydHM6IFtTSEFSRURfRElSRUNUSVZFU11cbiAgICB9KVxuXSwgU2hhcmVkRGlyZWN0aXZlc01vZHVsZSk7XG5cbi8qKlxuICogQGhpZGRlblxuICovXG5jb25zdCBwYWNrYWdlTWV0YWRhdGEgPSB7XG4gICAgbmFtZTogJ0Bwcm9ncmVzcy9rZW5kby1hbmd1bGFyLWxhYmVsJyxcbiAgICBwcm9kdWN0TmFtZTogJ0tlbmRvIFVJIGZvciBBbmd1bGFyJyxcbiAgICBwcm9kdWN0Q29kZXM6IFsnS0VORE9VSUFOR1VMQVInLCAnS0VORE9VSUNPTVBMRVRFJ10sXG4gICAgcHVibGlzaERhdGU6IDE2NDYyMTkwNTgsXG4gICAgdmVyc2lvbjogJycsXG4gICAgbGljZW5zaW5nRG9jc1VybDogJ2h0dHBzOi8vd3d3LnRlbGVyaWsuY29tL2tlbmRvLWFuZ3VsYXItdWkvbXktbGljZW5zZS8/dXRtX21lZGl1bT1wcm9kdWN0JnV0bV9zb3VyY2U9a2VuZG9hbmd1bGFyJnV0bV9jYW1wYWlnbj1rZW5kby11aS1hbmd1bGFyLXB1cmNoYXNlLWxpY2Vuc2Uta2V5cy13YXJuaW5nJ1xufTtcblxuLyoqXG4gKiBAaGlkZGVuXG4gKi9cbmNsYXNzIEZsb2F0aW5nTGFiZWxJbnB1dEFkYXB0ZXIge1xuICAgIGNvbnN0cnVjdG9yKGNvbXBvbmVudCwgZm9ybUNvbnRyb2wpIHtcbiAgICAgICAgdGhpcy5jb21wb25lbnQgPSBjb21wb25lbnQ7XG4gICAgICAgIGNvbnN0IGlzT2JzZXJ2YWJsZU9yRXZlbnRFbWl0dGVyID0gKGV2ZW50KSA9PiBldmVudCBpbnN0YW5jZW9mIE9ic2VydmFibGUgfHwgZXZlbnQgaW5zdGFuY2VvZiBFdmVudEVtaXR0ZXI7XG4gICAgICAgIGlmIChpc09ic2VydmFibGVPckV2ZW50RW1pdHRlcihjb21wb25lbnQub25Gb2N1cykpIHtcbiAgICAgICAgICAgIHRoaXMub25Gb2N1cyA9IGNvbXBvbmVudC5vbkZvY3VzO1xuICAgICAgICB9XG4gICAgICAgIGlmIChpc09ic2VydmFibGVPckV2ZW50RW1pdHRlcihjb21wb25lbnQuYXV0b0ZpbGxTdGFydCkpIHtcbiAgICAgICAgICAgIHRoaXMuYXV0b0ZpbGxTdGFydCA9IGNvbXBvbmVudC5hdXRvRmlsbFN0YXJ0O1xuICAgICAgICB9XG4gICAgICAgIGlmIChpc09ic2VydmFibGVPckV2ZW50RW1pdHRlcihjb21wb25lbnQuYXV0b0ZpbGxFbmQpKSB7XG4gICAgICAgICAgICB0aGlzLmF1dG9GaWxsRW5kID0gY29tcG9uZW50LmF1dG9GaWxsRW5kO1xuICAgICAgICB9XG4gICAgICAgIGlmIChpc09ic2VydmFibGVPckV2ZW50RW1pdHRlcihjb21wb25lbnQub25CbHVyKSkge1xuICAgICAgICAgICAgdGhpcy5vbkJsdXIgPSBjb21wb25lbnQub25CbHVyO1xuICAgICAgICB9XG4gICAgICAgIGlmIChmb3JtQ29udHJvbCkge1xuICAgICAgICAgICAgdGhpcy5vblZhbHVlQ2hhbmdlID0gZm9ybUNvbnRyb2wudmFsdWVDaGFuZ2VzO1xuICAgICAgICB9XG4gICAgICAgIGVsc2UgaWYgKGNvbXBvbmVudC5vblZhbHVlQ2hhbmdlKSB7XG4gICAgICAgICAgICB0aGlzLm9uVmFsdWVDaGFuZ2UgPSBjb21wb25lbnQub25WYWx1ZUNoYW5nZTtcbiAgICAgICAgfVxuICAgIH1cbiAgICBnZXQgZm9jdXNhYmxlSWQoKSB7XG4gICAgICAgIGNvbnN0IGNvbXBvbmVudCA9IHRoaXMuY29tcG9uZW50O1xuICAgICAgICBpZiAoJ2ZvY3VzYWJsZUlkJyBpbiBjb21wb25lbnQpIHtcbiAgICAgICAgICAgIHJldHVybiBjb21wb25lbnQuZm9jdXNhYmxlSWQ7XG4gICAgICAgIH1cbiAgICAgICAgZWxzZSBpZiAoJ2lkJyBpbiBjb21wb25lbnQpIHtcbiAgICAgICAgICAgIHJldHVybiBjb21wb25lbnQuaWQ7XG4gICAgICAgIH1cbiAgICAgICAgcmV0dXJuIFwiXCI7XG4gICAgfVxuICAgIHNldCBmb2N1c2FibGVJZCh2YWx1ZSkge1xuICAgICAgICBjb25zdCBjb21wb25lbnQgPSB0aGlzLmNvbXBvbmVudDtcbiAgICAgICAgaWYgKCdmb2N1c2FibGVJZCcgaW4gY29tcG9uZW50KSB7XG4gICAgICAgICAgICBjb21wb25lbnQuZm9jdXNhYmxlSWQgPSB2YWx1ZTtcbiAgICAgICAgfVxuICAgICAgICBlbHNlIGlmICgnaWQnIGluIGNvbXBvbmVudCkge1xuICAgICAgICAgICAgY29tcG9uZW50LmlkID0gdmFsdWU7XG4gICAgICAgIH1cbiAgICB9XG59XG5cbmNvbnN0IGlzRnVuY3Rpb24gPSAoeCkgPT4gT2JqZWN0LnByb3RvdHlwZS50b1N0cmluZy5jYWxsKHgpID09PSAnW29iamVjdCBGdW5jdGlvbl0nO1xuLyoqXG4gKiBSZXByZXNlbnRzIHRoZSBbS2VuZG8gVUkgRmxvYXRpbmdMYWJlbCBjb21wb25lbnQgZm9yIEFuZ3VsYXJdKHslIHNsdWcgb3ZlcnZpZXdfZmxvYXRpbmdsYWJlbCAlfSkuXG4gKiBQcm92aWRlcyBmbG9hdGluZyBsYWJlbHMgdG8gYGlucHV0YCBlbGVtZW50cy5cbiAqXG4gKiBUaGUgRmxvYXRpbmdMYWJlbCBzdXBwb3J0cyBib3RoIFRlbXBsYXRlIGFuZCBSZWFjdGl2ZSBGb3JtcyBhbmRcbiAqIFtjYW4gY29udGFpbiBLZW5kbyBVSSBmb3IgQW5ndWxhciBJbnB1dCBjb21wb25lbnRzIHN1Y2ggYXMgYGtlbmRvLWNvbWJvYm94YCBhbmQgYGtlbmRvLW51bWVyaWN0ZXh0Ym94YCxcbiAqIG9yIEhUTUwgSW5wdXQgZWxlbWVudHMgd2l0aCB0aGUgYGtlbmRvVGV4dEJveGAgZGlyZWN0aXZlIGFwcGxpZWRdKHslIHNsdWcgb3ZlcnZpZXdfZmxvYXRpbmdsYWJlbCAlfSN0b2MtaW1wbGVtZW50aW5nLWZsb2F0aW5nLWxhYmVscykuXG4gKlxuICogQGV4YW1wbGVcbiAqIGBgYHRzXG4gKlxuICogX0BDb21wb25lbnQoe1xuICogICBzZWxlY3RvcjogJ215LWFwcCcsXG4gKiAgIHRlbXBsYXRlOiBgXG4gKiAgICAgPGtlbmRvLWZsb2F0aW5nbGFiZWwgdGV4dD1cIkZpcnN0IG5hbWVcIj5cbiAqICAgICAgIDxpbnB1dCBbKG5nTW9kZWwpXT1cIm5hbWVcIiBrZW5kb1RleHRCb3ggLz5cbiAqICAgICA8L2tlbmRvLWZsb2F0aW5nbGFiZWw+XG4gKiAgIGBcbiAqIH0pXG4gKiBjbGFzcyBBcHBDb21wb25lbnQge1xuICogICAgIHB1YmxpYyBuYW1lID0gJ0pvaG4nO1xuICogfVxuICpcbiAqIGBgYFxuICovXG5sZXQgRmxvYXRpbmdMYWJlbENvbXBvbmVudCA9IGNsYXNzIEZsb2F0aW5nTGFiZWxDb21wb25lbnQge1xuICAgIGNvbnN0cnVjdG9yKGVsZW1lbnRSZWYsIHJlbmRlcmVyLCBjaGFuZ2VEZXRlY3RvclJlZiwgbG9jYWxpemF0aW9uKSB7XG4gICAgICAgIHRoaXMuZWxlbWVudFJlZiA9IGVsZW1lbnRSZWY7XG4gICAgICAgIHRoaXMucmVuZGVyZXIgPSByZW5kZXJlcjtcbiAgICAgICAgdGhpcy5jaGFuZ2VEZXRlY3RvclJlZiA9IGNoYW5nZURldGVjdG9yUmVmO1xuICAgICAgICB0aGlzLmxvY2FsaXphdGlvbiA9IGxvY2FsaXphdGlvbjtcbiAgICAgICAgdGhpcy5ob3N0Q2xhc3NlcyA9IHRydWU7XG4gICAgICAgIC8qKlxuICAgICAgICAgKiBGaXJlcyBhZnRlciB0aGUgZmxvYXRpbmcgbGFiZWwgcG9zaXRpb24gaXMgY2hhbmdlZC5cbiAgICAgICAgICovXG4gICAgICAgIHRoaXMucG9zaXRpb25DaGFuZ2UgPSBuZXcgRXZlbnRFbWl0dGVyKCk7XG4gICAgICAgIC8qKlxuICAgICAgICAgKiBAaGlkZGVuXG4gICAgICAgICAqL1xuICAgICAgICB0aGlzLmZvY3VzZWQgPSBmYWxzZTtcbiAgICAgICAgLyoqXG4gICAgICAgICAqIEBoaWRkZW5cbiAgICAgICAgICovXG4gICAgICAgIHRoaXMuZW1wdHkgPSB0cnVlO1xuICAgICAgICAvKipcbiAgICAgICAgICogQGhpZGRlblxuICAgICAgICAgKi9cbiAgICAgICAgdGhpcy5pbnZhbGlkID0gZmFsc2U7XG4gICAgICAgIC8qKlxuICAgICAgICAgKiBAaGlkZGVuXG4gICAgICAgICAqL1xuICAgICAgICB0aGlzLmxhYmVsSWQgPSBgay0ke2d1aWQoKX1gO1xuICAgICAgICB0aGlzLmF1dG9GaWxsU3RhcnRlZCA9IGZhbHNlO1xuICAgICAgICB2YWxpZGF0ZVBhY2thZ2UocGFja2FnZU1ldGFkYXRhKTtcbiAgICAgICAgdGhpcy5kaXJlY3Rpb24gPSBsb2NhbGl6YXRpb24ucnRsID8gJ3J0bCcgOiAnbHRyJztcbiAgICAgICAgdGhpcy5yZW5kZXJlci5yZW1vdmVBdHRyaWJ1dGUodGhpcy5lbGVtZW50UmVmLm5hdGl2ZUVsZW1lbnQsIFwiaWRcIik7XG4gICAgfVxuICAgIC8qKlxuICAgICAqIFJlcHJlc2VudHMgdGhlIGN1cnJlbnQgZmxvYXRpbmcgbGFiZWwgcG9zaXRpb24uXG4gICAgICovXG4gICAgZ2V0IGxhYmVsUG9zaXRpb24oKSB7XG4gICAgICAgIGlmICghdGhpcy5lbXB0eSkge1xuICAgICAgICAgICAgcmV0dXJuICdPdXQnO1xuICAgICAgICB9XG4gICAgICAgIHJldHVybiB0aGlzLmZvY3VzZWQgPyAnT3V0JyA6ICdJbic7XG4gICAgfVxuICAgIGdldCBmb2N1c2VkQ2xhc3MoKSB7XG4gICAgICAgIHJldHVybiB0aGlzLmZvY3VzZWQ7XG4gICAgfVxuICAgIGdldCBpbnZhbGlkQ2xhc3MoKSB7XG4gICAgICAgIHJldHVybiB0aGlzLmludmFsaWQ7XG4gICAgfVxuICAgIC8qKlxuICAgICAqIEBoaWRkZW5cbiAgICAgKi9cbiAgICBuZ0FmdGVyQ29udGVudEluaXQoKSB7XG4gICAgICAgIHRoaXMudmFsaWRhdGVTZXR1cCgpO1xuICAgICAgICBjb25zdCBjb250cm9sID0gbmV3IEZsb2F0aW5nTGFiZWxJbnB1dEFkYXB0ZXIodGhpcy5rZW5kb0lucHV0IHx8IHRoaXMuZm9ybUNvbnRyb2wudmFsdWVBY2Nlc3NvciwgdGhpcy5mb3JtQ29udHJvbCk7XG4gICAgICAgIHRoaXMuYWRkSGFuZGxlcnMoY29udHJvbCk7XG4gICAgICAgIHRoaXMuc2V0TGFiZWxGb3IoY29udHJvbCk7XG4gICAgfVxuICAgIG5nQWZ0ZXJWaWV3SW5pdCgpIHtcbiAgICAgICAgaWYgKHRoaXMua2VuZG9JbnB1dCkge1xuICAgICAgICAgICAgdGhpcy5zZXRBcmlhTGFiZWxsZWRieSh0aGlzLmtlbmRvSW5wdXQpO1xuICAgICAgICB9XG4gICAgfVxuICAgIC8qKlxuICAgICAqIEBoaWRkZW5cbiAgICAgKi9cbiAgICBuZ09uRGVzdHJveSgpIHtcbiAgICAgICAgaWYgKHRoaXMuc3Vic2NyaXB0aW9uKSB7XG4gICAgICAgICAgICB0aGlzLnN1YnNjcmlwdGlvbi51bnN1YnNjcmliZSgpO1xuICAgICAgICB9XG4gICAgfVxuICAgIC8qKlxuICAgICAqIEBoaWRkZW5cbiAgICAgKi9cbiAgICB0ZXh0Rm9yKGtleSkge1xuICAgICAgICByZXR1cm4gdGhpcy5sb2NhbGl6YXRpb24uZ2V0KGtleSk7XG4gICAgfVxuICAgIHN1YnNjcmliZShjb250cm9sLCBldmVudE5hbWUsIGhhbmRsZXIpIHtcbiAgICAgICAgaWYgKGNvbnRyb2xbZXZlbnROYW1lXSBpbnN0YW5jZW9mIEV2ZW50RW1pdHRlcikge1xuICAgICAgICAgICAgY29uc3Qgc3Vic2NyaXB0aW9uID0gY29udHJvbFtldmVudE5hbWVdLnN1YnNjcmliZShoYW5kbGVyKTtcbiAgICAgICAgICAgIGlmICghdGhpcy5zdWJzY3JpcHRpb24pIHtcbiAgICAgICAgICAgICAgICB0aGlzLnN1YnNjcmlwdGlvbiA9IHN1YnNjcmlwdGlvbjtcbiAgICAgICAgICAgIH1cbiAgICAgICAgICAgIGVsc2Uge1xuICAgICAgICAgICAgICAgIHRoaXMuc3Vic2NyaXB0aW9uLmFkZChzdWJzY3JpcHRpb24pO1xuICAgICAgICAgICAgfVxuICAgICAgICB9XG4gICAgfVxuICAgIHVwZGF0ZVN0YXRlKCkge1xuICAgICAgICBjb25zdCBlbXB0eSA9IHZhbHVlID0+IHtcbiAgICAgICAgICAgIC8vIHplcm8gaXMgbm90IGFuIGVtcHR5IHZhbHVlIChlLmcuLCBOdW1lcmljVGV4dEJveClcbiAgICAgICAgICAgIGlmICh2YWx1ZSA9PT0gMCB8fCB2YWx1ZSA9PT0gZmFsc2UpIHtcbiAgICAgICAgICAgICAgICByZXR1cm4gZmFsc2U7XG4gICAgICAgICAgICB9XG4gICAgICAgICAgICAvLyBlbXB0eSBhcnJheXMgYXJlIGFuIGVtcHR5IHZhbHVlIChlLmcuLCBNdWx0aVNlbGVjdClcbiAgICAgICAgICAgIGlmIChBcnJheS5pc0FycmF5KHZhbHVlKSAmJiAhdmFsdWUubGVuZ3RoKSB7XG4gICAgICAgICAgICAgICAgcmV0dXJuIHRydWU7XG4gICAgICAgICAgICB9XG4gICAgICAgICAgICByZXR1cm4gIXZhbHVlO1xuICAgICAgICB9O1xuICAgICAgICBjb25zdCBmb3JtQ29udHJvbCA9IHRoaXMuZm9ybUNvbnRyb2w7XG4gICAgICAgIGlmIChmb3JtQ29udHJvbCkge1xuICAgICAgICAgICAgY29uc3QgdmFsdWVBY2Nlc3NvciA9IGZvcm1Db250cm9sLnZhbHVlQWNjZXNzb3I7XG4gICAgICAgICAgICBpZiAoaXNGdW5jdGlvbih2YWx1ZUFjY2Vzc29yLmlzRW1wdHkpKSB7XG4gICAgICAgICAgICAgICAgdGhpcy5lbXB0eSA9IHZhbHVlQWNjZXNzb3IuaXNFbXB0eSgpO1xuICAgICAgICAgICAgfVxuICAgICAgICAgICAgZWxzZSB7XG4gICAgICAgICAgICAgICAgdGhpcy5lbXB0eSA9IGVtcHR5KGZvcm1Db250cm9sLnZhbHVlKTtcbiAgICAgICAgICAgIH1cbiAgICAgICAgICAgIHRoaXMuaW52YWxpZCA9IGZvcm1Db250cm9sLmludmFsaWQgJiYgKGZvcm1Db250cm9sLnRvdWNoZWQgfHwgZm9ybUNvbnRyb2wuZGlydHkpO1xuICAgICAgICB9XG4gICAgICAgIGVsc2Uge1xuICAgICAgICAgICAgdGhpcy5lbXB0eSA9IGlzRnVuY3Rpb24odGhpcy5rZW5kb0lucHV0LmlzRW1wdHkpID9cbiAgICAgICAgICAgICAgICB0aGlzLmtlbmRvSW5wdXQuaXNFbXB0eSgpIDogZW1wdHkodGhpcy5rZW5kb0lucHV0LnZhbHVlKTtcbiAgICAgICAgfVxuICAgICAgICBpZiAodGhpcy5lbXB0eSkge1xuICAgICAgICAgICAgdGhpcy5yZW5kZXJlci5hZGRDbGFzcyh0aGlzLmVsZW1lbnRSZWYubmF0aXZlRWxlbWVudCwgJ2stc3RhdGUtZW1wdHknKTtcbiAgICAgICAgfVxuICAgICAgICBlbHNlIHtcbiAgICAgICAgICAgIHRoaXMucmVuZGVyZXIucmVtb3ZlQ2xhc3ModGhpcy5lbGVtZW50UmVmLm5hdGl2ZUVsZW1lbnQsICdrLXN0YXRlLWVtcHR5Jyk7XG4gICAgICAgIH1cbiAgICAgICAgdGhpcy5jaGFuZ2VEZXRlY3RvclJlZi5tYXJrRm9yQ2hlY2soKTtcbiAgICB9XG4gICAgc2V0QXJpYUxhYmVsbGVkYnkoY29tcG9uZW50KSB7XG4gICAgICAgIGNvbnN0IGNvbXBvbmVudElkID0gY29tcG9uZW50LmZvY3VzYWJsZUlkIHx8IGNvbXBvbmVudC5pZDtcbiAgICAgICAgaWYgKGNvbXBvbmVudElkKSB7XG4gICAgICAgICAgICBjb25zdCBmb2N1c2FibGVFbGVtZW50ID0gdGhpcy5lbGVtZW50UmVmLm5hdGl2ZUVsZW1lbnQucXVlcnlTZWxlY3RvcihgIyR7Y29tcG9uZW50SWR9YCk7XG4gICAgICAgICAgICB0aGlzLnJlbmRlcmVyLnNldEF0dHJpYnV0ZShmb2N1c2FibGVFbGVtZW50LCAnYXJpYS1sYWJlbGxlZGJ5JywgdGhpcy5sYWJlbElkKTtcbiAgICAgICAgfVxuICAgIH1cbiAgICBzZXRMYWJlbEZvcihjb250cm9sKSB7XG4gICAgICAgIGNvbnN0IGNvbnRyb2xJZCA9IGNvbnRyb2wuZm9jdXNhYmxlSWQgfHwgY29udHJvbC5pZDtcbiAgICAgICAgaWYgKHRoaXMuaWQgJiYgY29udHJvbElkKSB7XG4gICAgICAgICAgICAvLyBpbnB1dCB3aW5zXG4gICAgICAgICAgICB0aGlzLmlkID0gY29udHJvbElkO1xuICAgICAgICB9XG4gICAgICAgIGVsc2UgaWYgKHRoaXMuaWQpIHtcbiAgICAgICAgICAgIGNvbnRyb2wuZm9jdXNhYmxlSWQgPSB0aGlzLmlkO1xuICAgICAgICB9XG4gICAgICAgIGVsc2UgaWYgKGNvbnRyb2xJZCkge1xuICAgICAgICAgICAgdGhpcy5pZCA9IGNvbnRyb2xJZDtcbiAgICAgICAgfVxuICAgICAgICBlbHNlIHtcbiAgICAgICAgICAgIGNvbnN0IGlkID0gYGstJHtndWlkKCl9YDtcbiAgICAgICAgICAgIGNvbnRyb2wuZm9jdXNhYmxlSWQgPSBpZDtcbiAgICAgICAgICAgIHRoaXMuaWQgPSBpZDtcbiAgICAgICAgfVxuICAgIH1cbiAgICBoYW5kbGVBdXRvZmlsbChjb250cm9sKSB7XG4gICAgICAgIHRoaXMuc3Vic2NyaWJlKGNvbnRyb2wsICdhdXRvRmlsbFN0YXJ0JywgKCkgPT4ge1xuICAgICAgICAgICAgdGhpcy5hdXRvRmlsbFN0YXJ0ZWQgPSB0cnVlO1xuICAgICAgICAgICAgdGhpcy5yZW5kZXJlci5yZW1vdmVDbGFzcyh0aGlzLmVsZW1lbnRSZWYubmF0aXZlRWxlbWVudCwgJ2stc3RhdGUtZW1wdHknKTtcbiAgICAgICAgfSk7XG4gICAgICAgIHRoaXMuc3Vic2NyaWJlKGNvbnRyb2wsICdhdXRvRmlsbEVuZCcsICgpID0+IHtcbiAgICAgICAgICAgIGlmICh0aGlzLmF1dG9GaWxsU3RhcnRlZCkge1xuICAgICAgICAgICAgICAgIHRoaXMuYXV0b0ZpbGxTdGFydGVkID0gZmFsc2U7XG4gICAgICAgICAgICAgICAgaWYgKHRoaXMuZW1wdHkpIHtcbiAgICAgICAgICAgICAgICAgICAgdGhpcy5yZW5kZXJlci5hZGRDbGFzcyh0aGlzLmVsZW1lbnRSZWYubmF0aXZlRWxlbWVudCwgJ2stc3RhdGUtZW1wdHknKTtcbiAgICAgICAgICAgICAgICB9XG4gICAgICAgICAgICB9XG4gICAgICAgIH0pO1xuICAgIH1cbiAgICBhZGRIYW5kbGVycyhjb250cm9sKSB7XG4gICAgICAgIGNvbnN0IHNldEZvY3VzID0gKGlzRm9jdXNlZCkgPT4gKCkgPT4ge1xuICAgICAgICAgICAgdGhpcy5mb2N1c2VkID0gaXNGb2N1c2VkO1xuICAgICAgICAgICAgdGhpcy51cGRhdGVTdGF0ZSgpO1xuICAgICAgICAgICAgaWYgKCF0aGlzLmVtcHR5KSB7XG4gICAgICAgICAgICAgICAgcmV0dXJuO1xuICAgICAgICAgICAgfVxuICAgICAgICAgICAgaWYgKGhhc09ic2VydmVycyh0aGlzLnBvc2l0aW9uQ2hhbmdlKSkge1xuICAgICAgICAgICAgICAgIHRoaXMucG9zaXRpb25DaGFuZ2UuZW1pdChpc0ZvY3VzZWQgPyAnT3V0JyA6ICdJbicpO1xuICAgICAgICAgICAgfVxuICAgICAgICB9O1xuICAgICAgICB0aGlzLnN1YnNjcmliZShjb250cm9sLCAnb25Gb2N1cycsIHNldEZvY3VzKHRydWUpKTtcbiAgICAgICAgdGhpcy5zdWJzY3JpYmUoY29udHJvbCwgJ29uQmx1cicsIHNldEZvY3VzKGZhbHNlKSk7XG4gICAgICAgIHRoaXMuaGFuZGxlQXV0b2ZpbGwoY29udHJvbCk7XG4gICAgICAgIGNvbnN0IHVwZGF0ZVN0YXRlID0gKCkgPT4gdGhpcy51cGRhdGVTdGF0ZSgpO1xuICAgICAgICB1cGRhdGVTdGF0ZSgpO1xuICAgICAgICB0aGlzLnN1YnNjcmliZShjb250cm9sLCAnb25WYWx1ZUNoYW5nZScsIHVwZGF0ZVN0YXRlKTtcbiAgICB9XG4gICAgdmFsaWRhdGVTZXR1cCgpIHtcbiAgICAgICAgaWYgKCF0aGlzLmZvcm1Db250cm9sICYmICF0aGlzLmtlbmRvSW5wdXQpIHtcbiAgICAgICAgICAgIGlmIChpc0Rldk1vZGUoKSkge1xuICAgICAgICAgICAgICAgIHRocm93IG5ldyBFcnJvcihcIlRoZSBGbG9hdGluZ0xhYmVsQ29tcG9uZW50IHJlcXVpcmVzIGEgS2VuZG8gSW5wdXQgY29tcG9uZW50XCIgK1xuICAgICAgICAgICAgICAgICAgICBcIiBvciBhIGZvcm1zLWJvdW5kIGNvbXBvbmVudCB0byBmdW5jdGlvbiBwcm9wZXJseS5cIik7XG4gICAgICAgICAgICB9XG4gICAgICAgICAgICByZXR1cm47XG4gICAgICAgIH1cbiAgICB9XG59O1xuX19kZWNvcmF0ZShbXG4gICAgSG9zdEJpbmRpbmcoJ2NsYXNzLmstZmxvYXRpbmctbGFiZWwtY29udGFpbmVyJyksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIEJvb2xlYW4pXG5dLCBGbG9hdGluZ0xhYmVsQ29tcG9uZW50LnByb3RvdHlwZSwgXCJob3N0Q2xhc3Nlc1wiLCB2b2lkIDApO1xuX19kZWNvcmF0ZShbXG4gICAgSG9zdEJpbmRpbmcoJ2NsYXNzLmstc3RhdGUtZm9jdXNlZCcpLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246dHlwZVwiLCBCb29sZWFuKSxcbiAgICBfX21ldGFkYXRhKFwiZGVzaWduOnBhcmFtdHlwZXNcIiwgW10pXG5dLCBGbG9hdGluZ0xhYmVsQ29tcG9uZW50LnByb3RvdHlwZSwgXCJmb2N1c2VkQ2xhc3NcIiwgbnVsbCk7XG5fX2RlY29yYXRlKFtcbiAgICBIb3N0QmluZGluZygnY2xhc3Muay1zdGF0ZS1pbnZhbGlkJyksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIEJvb2xlYW4pLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246cGFyYW10eXBlc1wiLCBbXSlcbl0sIEZsb2F0aW5nTGFiZWxDb21wb25lbnQucHJvdG90eXBlLCBcImludmFsaWRDbGFzc1wiLCBudWxsKTtcbl9fZGVjb3JhdGUoW1xuICAgIEhvc3RCaW5kaW5nKCdhdHRyLmRpcicpLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246dHlwZVwiLCBTdHJpbmcpXG5dLCBGbG9hdGluZ0xhYmVsQ29tcG9uZW50LnByb3RvdHlwZSwgXCJkaXJlY3Rpb25cIiwgdm9pZCAwKTtcbl9fZGVjb3JhdGUoW1xuICAgIElucHV0KCksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIE9iamVjdClcbl0sIEZsb2F0aW5nTGFiZWxDb21wb25lbnQucHJvdG90eXBlLCBcImxhYmVsQ3NzU3R5bGVcIiwgdm9pZCAwKTtcbl9fZGVjb3JhdGUoW1xuICAgIElucHV0KCksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIE9iamVjdClcbl0sIEZsb2F0aW5nTGFiZWxDb21wb25lbnQucHJvdG90eXBlLCBcImxhYmVsQ3NzQ2xhc3NcIiwgdm9pZCAwKTtcbl9fZGVjb3JhdGUoW1xuICAgIElucHV0KCksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIFN0cmluZylcbl0sIEZsb2F0aW5nTGFiZWxDb21wb25lbnQucHJvdG90eXBlLCBcImlkXCIsIHZvaWQgMCk7XG5fX2RlY29yYXRlKFtcbiAgICBJbnB1dCgpLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246dHlwZVwiLCBTdHJpbmcpXG5dLCBGbG9hdGluZ0xhYmVsQ29tcG9uZW50LnByb3RvdHlwZSwgXCJ0ZXh0XCIsIHZvaWQgMCk7XG5fX2RlY29yYXRlKFtcbiAgICBJbnB1dCgpLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246dHlwZVwiLCBCb29sZWFuKVxuXSwgRmxvYXRpbmdMYWJlbENvbXBvbmVudC5wcm90b3R5cGUsIFwib3B0aW9uYWxcIiwgdm9pZCAwKTtcbl9fZGVjb3JhdGUoW1xuICAgIE91dHB1dCgpLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246dHlwZVwiLCBFdmVudEVtaXR0ZXIpXG5dLCBGbG9hdGluZ0xhYmVsQ29tcG9uZW50LnByb3RvdHlwZSwgXCJwb3NpdGlvbkNoYW5nZVwiLCB2b2lkIDApO1xuX19kZWNvcmF0ZShbXG4gICAgQ29udGVudENoaWxkKEtlbmRvSW5wdXQsIHsgc3RhdGljOiBmYWxzZSB9KSxcbiAgICBfX21ldGFkYXRhKFwiZGVzaWduOnR5cGVcIiwgT2JqZWN0KVxuXSwgRmxvYXRpbmdMYWJlbENvbXBvbmVudC5wcm90b3R5cGUsIFwia2VuZG9JbnB1dFwiLCB2b2lkIDApO1xuX19kZWNvcmF0ZShbXG4gICAgQ29udGVudENoaWxkKE5nQ29udHJvbCwgeyBzdGF0aWM6IGZhbHNlIH0pLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246dHlwZVwiLCBOZ0NvbnRyb2wpXG5dLCBGbG9hdGluZ0xhYmVsQ29tcG9uZW50LnByb3RvdHlwZSwgXCJmb3JtQ29udHJvbFwiLCB2b2lkIDApO1xuRmxvYXRpbmdMYWJlbENvbXBvbmVudCA9IF9fZGVjb3JhdGUoW1xuICAgIENvbXBvbmVudCh7XG4gICAgICAgIHNlbGVjdG9yOiAna2VuZG8tZmxvYXRpbmdsYWJlbCcsXG4gICAgICAgIGV4cG9ydEFzOiAna2VuZG9GbG9hdGluZ0xhYmVsJyxcbiAgICAgICAgcHJvdmlkZXJzOiBbXG4gICAgICAgICAgICBMb2NhbGl6YXRpb25TZXJ2aWNlLFxuICAgICAgICAgICAge1xuICAgICAgICAgICAgICAgIHByb3ZpZGU6IEwxME5fUFJFRklYLFxuICAgICAgICAgICAgICAgIHVzZVZhbHVlOiAna2VuZG8uZmxvYXRpbmdsYWJlbCdcbiAgICAgICAgICAgIH1cbiAgICAgICAgXSxcbiAgICAgICAgdGVtcGxhdGU6IGBcbiAgICAgICAgPG5nLWNvbnRhaW5lciBrZW5kb0Zsb2F0aW5nTGFiZWxMb2NhbGl6ZWRNZXNzYWdlc1xuICAgICAgICAgICAgaTE4bi1vcHRpb25hbD1cImtlbmRvLmZsb2F0aW5nbGFiZWwub3B0aW9uYWx8VGhlIHRleHQgZm9yIHRoZSBvcHRpb25hbCBzZWdtZW50IG9mIGEgRmxvYXRpbmdMYWJlbCBjb21wb25lbnRcIlxuICAgICAgICAgICAgb3B0aW9uYWw9XCJPcHRpb25hbFwiXG4gICAgICAgICA+XG4gICAgICAgIDwvbmctY29udGFpbmVyPlxuICAgICAgICA8bmctY29udGVudD48L25nLWNvbnRlbnQ+XG4gICAgICAgIDxsYWJlbCAqbmdJZj1cInRleHRcIiBbbmdDbGFzc109XCJsYWJlbENzc0NsYXNzXCIgW25nU3R5bGVdPVwibGFiZWxDc3NTdHlsZVwiIFtmb3JdPVwiaWRcIiBbYXR0ci5pZF09XCJsYWJlbElkXCIgY2xhc3M9XCJrLWxhYmVsXCI+XG4gICAgICAgICAgICB7eyB0ZXh0IH19PHNwYW4gKm5nSWY9XCJvcHRpb25hbFwiIGNsYXNzPVwiay1sYWJlbC1vcHRpb25hbFwiPih7e3RleHRGb3IoJ29wdGlvbmFsJyl9fSk8L3NwYW4+XG4gICAgICAgIDwvbGFiZWw+XG4gICAgYFxuICAgIH0pLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246cGFyYW10eXBlc1wiLCBbRWxlbWVudFJlZixcbiAgICAgICAgUmVuZGVyZXIyLFxuICAgICAgICBDaGFuZ2VEZXRlY3RvclJlZixcbiAgICAgICAgTG9jYWxpemF0aW9uU2VydmljZV0pXG5dLCBGbG9hdGluZ0xhYmVsQ29tcG9uZW50KTtcblxuY29uc3QgQ09NUE9ORU5UX0RJUkVDVElWRVMgPSBbRmxvYXRpbmdMYWJlbENvbXBvbmVudF07XG4vKipcbiAqIFJlcHJlc2VudHMgdGhlIFtOZ01vZHVsZV0oe3sgc2l0ZS5kYXRhLnVybHMuYW5ndWxhclsnbmdtb2R1bGVhcGknXSB9fSlcbiAqIGRlZmluaXRpb24gZm9yIHRoZSBUZXh0Qm94IGRpcmVjdGl2ZS5cbiAqXG4gKiBAZXhhbXBsZVxuICpcbiAqIGBgYHRzLW5vLXJ1blxuICpcbiAqIC8vIFRoZSBicm93c2VyIHBsYXRmb3JtIHdpdGggYSBjb21waWxlclxuICogaW1wb3J0IHsgcGxhdGZvcm1Ccm93c2VyRHluYW1pYyB9IGZyb20gJ0Bhbmd1bGFyL3BsYXRmb3JtLWJyb3dzZXItZHluYW1pYyc7XG4gKlxuICogaW1wb3J0IHsgTmdNb2R1bGUgfSBmcm9tICdAYW5ndWxhci9jb3JlJztcbiAqXG4gKiAvLyBJbXBvcnQgdGhlIGFwcCBjb21wb25lbnRcbiAqIGltcG9ydCB7IEFwcENvbXBvbmVudCB9IGZyb20gJy4vYXBwLmNvbXBvbmVudCc7XG4gKlxuICogLy8gRGVmaW5lIHRoZSBhcHAgbW9kdWxlXG4gKiBfQE5nTW9kdWxlKHtcbiAqICAgICBkZWNsYXJhdGlvbnM6IFtBcHBDb21wb25lbnRdLCAvLyBkZWNsYXJlIGFwcCBjb21wb25lbnRcbiAqICAgICBpbXBvcnRzOiAgICAgIFtCcm93c2VyTW9kdWxlLCBGbG9hdGluZ0xhYmVsTW9kdWxlXSwgLy8gaW1wb3J0IEZsb2F0aW5nTGFiZWwgbW9kdWxlXG4gKiAgICAgYm9vdHN0cmFwOiAgICBbQXBwQ29tcG9uZW50XVxuICogfSlcbiAqIGV4cG9ydCBjbGFzcyBBcHBNb2R1bGUge31cbiAqXG4gKiAvLyBDb21waWxlIGFuZCBsYXVuY2ggdGhlIG1vZHVsZVxuICogcGxhdGZvcm1Ccm93c2VyRHluYW1pYygpLmJvb3RzdHJhcE1vZHVsZShBcHBNb2R1bGUpO1xuICpcbiAqIGBgYFxuICovXG5sZXQgRmxvYXRpbmdMYWJlbE1vZHVsZSA9IGNsYXNzIEZsb2F0aW5nTGFiZWxNb2R1bGUge1xufTtcbkZsb2F0aW5nTGFiZWxNb2R1bGUgPSBfX2RlY29yYXRlKFtcbiAgICBOZ01vZHVsZSh7XG4gICAgICAgIGRlY2xhcmF0aW9uczogWy4uLkNPTVBPTkVOVF9ESVJFQ1RJVkVTXSxcbiAgICAgICAgZXhwb3J0czogWy4uLkNPTVBPTkVOVF9ESVJFQ1RJVkVTLCBTaGFyZWREaXJlY3RpdmVzTW9kdWxlXSxcbiAgICAgICAgaW1wb3J0czogW0NvbW1vbk1vZHVsZSwgU2hhcmVkRGlyZWN0aXZlc01vZHVsZV1cbiAgICB9KVxuXSwgRmxvYXRpbmdMYWJlbE1vZHVsZSk7XG5cbi8qKlxuICogUmVwcmVzZW50cyB0aGUgW0tlbmRvIFVJIExhYmVsIGNvbXBvbmVudCBmb3IgQW5ndWxhcl0oeyUgc2x1ZyBsYWJlbF9iYXNpYyAlfSkuXG4gKlxuICogQXNzb2NpYXRlcyBhIGxhYmVsIHdpdGggaW5wdXQgZWxlbWVudHMgb3IgY29tcG9uZW50cy5cbiAqXG4gKiBAZXhhbXBsZVxuICogYGBgdHNcbiAqXG4gKiBfQENvbXBvbmVudCh7XG4gKiAgIHNlbGVjdG9yOiAnbXktYXBwJyxcbiAqICAgdGVtcGxhdGU6IGBcbiAqICAgICA8a2VuZG8tbGFiZWwgW2Zvcl09XCJpbnB1dFwiIHRleHQ9XCJGaXJzdCBuYW1lXCI+XG4gKiAgICAgICA8aW5wdXQgWyhuZ01vZGVsKV09XCJuYW1lXCIga2VuZG9UZXh0Qm94ICNpbnB1dCAvPlxuICogICAgIDwva2VuZG8tbGFiZWw+XG4gKiAgIGBcbiAqIH0pXG4gKiBjbGFzcyBBcHBDb21wb25lbnQge1xuICogICAgIHB1YmxpYyBuYW1lID0gJ0pvaG4nO1xuICogfVxuICpcbiAqIGBgYFxuICovXG5sZXQgTGFiZWxDb21wb25lbnQgPSBjbGFzcyBMYWJlbENvbXBvbmVudCB7XG4gICAgY29uc3RydWN0b3IoZWxlbWVudFJlZiwgcmVuZGVyZXIsIGxvY2FsaXphdGlvbikge1xuICAgICAgICB0aGlzLmVsZW1lbnRSZWYgPSBlbGVtZW50UmVmO1xuICAgICAgICB0aGlzLnJlbmRlcmVyID0gcmVuZGVyZXI7XG4gICAgICAgIHRoaXMubG9jYWxpemF0aW9uID0gbG9jYWxpemF0aW9uO1xuICAgICAgICB0aGlzLnN1YnNjcmlwdGlvbnMgPSBuZXcgU3Vic2NyaXB0aW9uKCk7XG4gICAgICAgIHZhbGlkYXRlUGFja2FnZShwYWNrYWdlTWV0YWRhdGEpO1xuICAgICAgICB0aGlzLmRpcmVjdGlvbiA9IGxvY2FsaXphdGlvbi5ydGwgPyAncnRsJyA6ICdsdHInO1xuICAgICAgICB0aGlzLnJlbmRlcmVyLnJlbW92ZUF0dHJpYnV0ZSh0aGlzLmVsZW1lbnRSZWYubmF0aXZlRWxlbWVudCwgJ2lkJyk7XG4gICAgfVxuICAgIC8qKlxuICAgICAqIEBoaWRkZW5cbiAgICAgKi9cbiAgICBuZ0FmdGVyQ29udGVudEluaXQoKSB7XG4gICAgICAgIGlmICh0aGlzLmZvcikge1xuICAgICAgICAgICAgdGhpcy5jb250cm9sID0gdGhpcy5mb3I7XG4gICAgICAgICAgICByZXR1cm47XG4gICAgICAgIH1cbiAgICAgICAgY29uc3Qgd3JhcHBlZE5hdGl2ZUlucHV0ID0gZ2V0V3JhcHBlZE5hdGl2ZUlucHV0KHRoaXMuZWxlbWVudFJlZi5uYXRpdmVFbGVtZW50KTtcbiAgICAgICAgaWYgKHdyYXBwZWROYXRpdmVJbnB1dCkge1xuICAgICAgICAgICAgaWYgKCF3cmFwcGVkTmF0aXZlSW5wdXQuaGFzQXR0cmlidXRlKCdpZCcpKSB7XG4gICAgICAgICAgICAgICAgdGhpcy5yZW5kZXJlci5zZXRBdHRyaWJ1dGUod3JhcHBlZE5hdGl2ZUlucHV0LCAnaWQnLCBgay0ke2d1aWQoKX1gKTtcbiAgICAgICAgICAgIH1cbiAgICAgICAgICAgIHRoaXMuY29udHJvbCA9IHdyYXBwZWROYXRpdmVJbnB1dDtcbiAgICAgICAgICAgIHJldHVybjtcbiAgICAgICAgfVxuICAgICAgICB0aGlzLmNvbnRyb2wgPSB0aGlzLmtlbmRvSW5wdXQ7XG4gICAgfVxuICAgIC8qKlxuICAgICAqIEBoaWRkZW5cbiAgICAgKi9cbiAgICBuZ09uSW5pdCgpIHtcbiAgICAgICAgdGhpcy5zdWJzY3JpcHRpb25zLmFkZCh0aGlzLmxvY2FsaXphdGlvbi5jaGFuZ2VzLnN1YnNjcmliZSgoeyBydGwgfSkgPT4ge1xuICAgICAgICAgICAgdGhpcy5kaXJlY3Rpb24gPSBydGwgPyAncnRsJyA6ICdsdHInO1xuICAgICAgICB9KSk7XG4gICAgfVxuICAgIC8qKlxuICAgICAqIEBoaWRkZW5cbiAgICAgKi9cbiAgICBuZ0FmdGVyVmlld0luaXQoKSB7XG4gICAgICAgIHRoaXMubGFiZWxEaXJlY3RpdmUuc2V0QXJpYUxhYmVsbGVkYnkoKTtcbiAgICB9XG4gICAgLyoqXG4gICAgICogQGhpZGRlblxuICAgICAqL1xuICAgIG5nT25EZXN0cm95KCkge1xuICAgICAgICBpZiAodGhpcy5zdWJzY3JpcHRpb25zKSB7XG4gICAgICAgICAgICB0aGlzLnN1YnNjcmlwdGlvbnMudW5zdWJzY3JpYmUoKTtcbiAgICAgICAgfVxuICAgIH1cbiAgICAvKipcbiAgICAgKiBAaGlkZGVuXG4gICAgICovXG4gICAgdGV4dEZvcihrZXkpIHtcbiAgICAgICAgcmV0dXJuIHRoaXMubG9jYWxpemF0aW9uLmdldChrZXkpO1xuICAgIH1cbn07XG5fX2RlY29yYXRlKFtcbiAgICBIb3N0QmluZGluZygnYXR0ci5kaXInKSxcbiAgICBfX21ldGFkYXRhKFwiZGVzaWduOnR5cGVcIiwgU3RyaW5nKVxuXSwgTGFiZWxDb21wb25lbnQucHJvdG90eXBlLCBcImRpcmVjdGlvblwiLCB2b2lkIDApO1xuX19kZWNvcmF0ZShbXG4gICAgSW5wdXQoKSxcbiAgICBfX21ldGFkYXRhKFwiZGVzaWduOnR5cGVcIiwgU3RyaW5nKVxuXSwgTGFiZWxDb21wb25lbnQucHJvdG90eXBlLCBcInRleHRcIiwgdm9pZCAwKTtcbl9fZGVjb3JhdGUoW1xuICAgIElucHV0KCksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIE9iamVjdClcbl0sIExhYmVsQ29tcG9uZW50LnByb3RvdHlwZSwgXCJmb3JcIiwgdm9pZCAwKTtcbl9fZGVjb3JhdGUoW1xuICAgIElucHV0KCksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIEJvb2xlYW4pXG5dLCBMYWJlbENvbXBvbmVudC5wcm90b3R5cGUsIFwib3B0aW9uYWxcIiwgdm9pZCAwKTtcbl9fZGVjb3JhdGUoW1xuICAgIFZpZXdDaGlsZChMYWJlbERpcmVjdGl2ZSwgeyBzdGF0aWM6IHRydWUgfSksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIExhYmVsRGlyZWN0aXZlKVxuXSwgTGFiZWxDb21wb25lbnQucHJvdG90eXBlLCBcImxhYmVsRGlyZWN0aXZlXCIsIHZvaWQgMCk7XG5fX2RlY29yYXRlKFtcbiAgICBDb250ZW50Q2hpbGQoS2VuZG9JbnB1dCwgeyBzdGF0aWM6IHRydWUgfSksXG4gICAgX19tZXRhZGF0YShcImRlc2lnbjp0eXBlXCIsIE9iamVjdClcbl0sIExhYmVsQ29tcG9uZW50LnByb3RvdHlwZSwgXCJrZW5kb0lucHV0XCIsIHZvaWQgMCk7XG5MYWJlbENvbXBvbmVudCA9IF9fZGVjb3JhdGUoW1xuICAgIENvbXBvbmVudCh7XG4gICAgICAgIHNlbGVjdG9yOiAna2VuZG8tbGFiZWwnLFxuICAgICAgICBleHBvcnRBczogJ2tlbmRvTGFiZWwnLFxuICAgICAgICBwcm92aWRlcnM6IFtcbiAgICAgICAgICAgIExvY2FsaXphdGlvblNlcnZpY2UsXG4gICAgICAgICAgICB7XG4gICAgICAgICAgICAgICAgcHJvdmlkZTogTDEwTl9QUkVGSVgsXG4gICAgICAgICAgICAgICAgdXNlVmFsdWU6ICdrZW5kby5sYWJlbCdcbiAgICAgICAgICAgIH1cbiAgICAgICAgXSxcbiAgICAgICAgdGVtcGxhdGU6IGBcbiAgICAgICAgPG5nLWNvbnRhaW5lciBrZW5kb0xhYmVsTG9jYWxpemVkTWVzc2FnZXNcbiAgICAgICAgICAgIGkxOG4tb3B0aW9uYWw9XCJrZW5kby5sYWJlbC5vcHRpb25hbHxUaGUgdGV4dCBmb3IgdGhlIG9wdGlvbmFsIHNlZ21lbnQgb2YgYSBMYWJlbCBjb21wb25lbnRcIlxuICAgICAgICAgICAgb3B0aW9uYWw9XCJPcHRpb25hbFwiXG4gICAgICAgICA+XG4gICAgICAgIDwvbmctY29udGFpbmVyPlxuICAgICAgICA8bGFiZWxcbiAgICAgICAgICAgIFtmb3JdPVwiY29udHJvbFwiXG4gICAgICAgICAgICBbY2xhc3Muay1sYWJlbC1lbXB0eV09XCIhdGV4dFwiPlxuICAgICAgICAgICAge3sgdGV4dCB9fTxzcGFuICpuZ0lmPVwib3B0aW9uYWxcIiBjbGFzcz1cImstbGFiZWwtb3B0aW9uYWxcIj4oe3t0ZXh0Rm9yKCdvcHRpb25hbCcpfX0pPC9zcGFuPlxuICAgICAgICA8L2xhYmVsPlxuICAgICAgICA8bmctY29udGVudD48L25nLWNvbnRlbnQ+XG4gICAgYFxuICAgIH0pLFxuICAgIF9fbWV0YWRhdGEoXCJkZXNpZ246cGFyYW10eXBlc1wiLCBbRWxlbWVudFJlZixcbiAgICAgICAgUmVuZGVyZXIyLFxuICAgICAgICBMb2NhbGl6YXRpb25TZXJ2aWNlXSlcbl0sIExhYmVsQ29tcG9uZW50KTtcblxuY29uc3QgQ09NUE9ORU5UX0RJUkVDVElWRVMkMSA9IFtcbiAgICBMYWJlbERpcmVjdGl2ZSxcbiAgICBMYWJlbENvbXBvbmVudFxuXTtcbi8qKlxuICogVGhlIGV4cG9ydGVkIHBhY2thZ2UgbW9kdWxlLlxuICpcbiAqIFRoZSBwYWNrYWdlIGV4cG9ydHM6XG4gKiAtIGBMYWJlbERpcmVjdGl2ZWAmbWRhc2g7VGhlIExhYmVsIGRpcmVjdGl2ZSBjbGFzcy5cbiAqIC0gYExhYmVsQ29tcG9uZW50YCZtZGFzaDtUaGUgTGFiZWwgY29tcG9uZW50IGNsYXNzXG4gKiAtIGBGTG9hdGluZ0xhYmVsYCZtZGFzaDtUaGUgRmxvYXRpbmdMYWJlbCBjb21wb25lbnQgY2xhc3MuXG4gKlxuICogQGV4YW1wbGVcbiAqXG4gKiBgYGB0cy1uby1ydW5cbiAqIC8vIEltcG9ydCB0aGUgTGFiZWwgbW9kdWxlXG4gKiBpbXBvcnQgeyBMYWJlbE1vZHVsZSB9IGZyb20gJ0Bwcm9ncmVzcy9rZW5kby1hbmd1bGFyLWxhYmVsJztcbiAqXG4gKiAvLyBUaGUgYnJvd3NlciBwbGF0Zm9ybSB3aXRoIGEgY29tcGlsZXJcbiAqIGltcG9ydCB7IHBsYXRmb3JtQnJvd3NlckR5bmFtaWMgfSBmcm9tICdAYW5ndWxhci9wbGF0Zm9ybS1icm93c2VyLWR5bmFtaWMnO1xuICpcbiAqIGltcG9ydCB7IE5nTW9kdWxlIH0gZnJvbSAnQGFuZ3VsYXIvY29yZSc7XG4gKlxuICogLy8gSW1wb3J0IHRoZSBhcHAgY29tcG9uZW50XG4gKiBpbXBvcnQgeyBBcHBDb21wb25lbnQgfSBmcm9tICcuL2FwcC5jb21wb25lbnQnO1xuICpcbiAqIC8vIERlZmluZSB0aGUgYXBwIG1vZHVsZVxuICogX0BOZ01vZHVsZSh7XG4gKiAgICAgZGVjbGFyYXRpb25zOiBbQXBwQ29tcG9uZW50XSwgLy8gZGVjbGFyZSBhcHAgY29tcG9uZW50XG4gKiAgICAgaW1wb3J0czogICAgICBbQnJvd3Nlck1vZHVsZSwgTGFiZWxNb2R1bGVdLCAvLyBpbXBvcnQgTGFiZWwgbW9kdWxlXG4gKiAgICAgYm9vdHN0cmFwOiAgICBbQXBwQ29tcG9uZW50XVxuICogfSlcbiAqIGV4cG9ydCBjbGFzcyBBcHBNb2R1bGUge31cbiAqXG4gKiAvLyBDb21waWxlIGFuZCBsYXVuY2ggdGhlIG1vZHVsZVxuICogcGxhdGZvcm1Ccm93c2VyRHluYW1pYygpLmJvb3RzdHJhcE1vZHVsZShBcHBNb2R1bGUpO1xuICpcbiAqIGBgYFxuICovXG5sZXQgTGFiZWxNb2R1bGUgPSBjbGFzcyBMYWJlbE1vZHVsZSB7XG59O1xuTGFiZWxNb2R1bGUgPSBfX2RlY29yYXRlKFtcbiAgICBOZ01vZHVsZSh7XG4gICAgICAgIGltcG9ydHM6IFtDb21tb25Nb2R1bGUsIFNoYXJlZERpcmVjdGl2ZXNNb2R1bGVdLFxuICAgICAgICBkZWNsYXJhdGlvbnM6IFsuLi5DT01QT05FTlRfRElSRUNUSVZFUyQxXSxcbiAgICAgICAgZXhwb3J0czogWy4uLkNPTVBPTkVOVF9ESVJFQ1RJVkVTJDEsIEZsb2F0aW5nTGFiZWxNb2R1bGUsIFNoYXJlZERpcmVjdGl2ZXNNb2R1bGVdXG4gICAgfSlcbl0sIExhYmVsTW9kdWxlKTtcblxuLyoqXG4gKiBHZW5lcmF0ZWQgYnVuZGxlIGluZGV4LiBEbyBub3QgZWRpdC5cbiAqL1xuXG5leHBvcnQgeyBMb2NhbGl6ZWRNZXNzYWdlc0RpcmVjdGl2ZSwgTWVzc2FnZXMsIFNoYXJlZERpcmVjdGl2ZXNNb2R1bGUsIExhYmVsRGlyZWN0aXZlLCBMYWJlbE1vZHVsZSwgRmxvYXRpbmdMYWJlbE1vZHVsZSwgRmxvYXRpbmdMYWJlbENvbXBvbmVudCwgTGFiZWxDb21wb25lbnQsIEN1c3RvbU1lc3NhZ2VzQ29tcG9uZW50IH07XG4iXX0=